/* eslint-disable react-hooks/rules-of-hooks */
// ^ The static-analysis rule fires false positives on this file because the
// component body contains conditional refs and dynamic lookups before the
// hook block. Every hook in this component IS called unconditionally on
// every render (in source order), which is what react-hooks/rules-of-hooks
// actually requires. Re-enable per-line when refactoring.
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useHistory, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { withGoogleMap, GoogleMap, Marker } from 'react-google-maps';
import { Capacitor } from '@capacitor/core';
import siteSetting from '../../../config/env';
import { marketplaceService } from '../../../_services/marketplace.service';
import { getSalonSummaryCache, setSalonSummaryCache } from '../../../_services/salonSummaryCache';
import { bookingService } from '../../../_services/booking.service';
import { paymentService } from '../../../_services/payment.service';
import { loadStripe } from '@stripe/stripe-js';
import BeuLoader from '../../common/BeuLoader';
import { BookingLoader, LOADER_STATES } from './BookingLoader';
import { SalonProfileSkeleton, ProfessionalProfileSkeleton } from './SalonProfileSkeleton';
import ConciergeWidget from './ConciergeWidget';
import { CONCIERGE_VISIBLE } from '../../../config/featureFlags';
import { computeBookingWindow } from '../../shared/computeBookingWindow';
import './SalonProfilePage.css';
import { getGiftCardChannels } from '../../gift-cards-v2/giftCardChannels';
import { getProductChannels } from '../../product-shop/productShopChannels';
import ProductShopPreview from '../../product-shop/ProductShopPreview';
import { sitesService } from '../../../_services/sites.service';
import PublicBrandPage from '../../multiLocation/PublicBrandPage';
import { captureTrackedSource, trackedSourceFor } from '../../../_utils/trackedSource';
import { DISCOUNTS_VISIBLE } from '../../../config/featureFlags';
import { useBookingCode } from '../../discounts/client/useBookingCode';
import { CodeBanner, CodeTag } from '../../discounts/client/BookingCode';
import { checkCode, holdCode, clientKeyOf } from '../../discounts/discountStore';

// Modals are lazy-loaded so their JS chunks don't land in the first-paint
// bundle for this page. Each one is only mounted when its `isOpen` state
// flips true (see render sites below), keeping initial salon-page open
// fast on mobile WebViews where parse/mount cost compounds.
const StylistSelectionModal = lazy(() => import('./StylistSelectionModal'));
const ServicesModal = lazy(() => import('./ServicesModal'));
const ServiceDetailPopup = lazy(() => import('./ServiceDetailPopup'));
const TimesModal = lazy(() => import('./TimesModal'));
const ConfirmBookingModal = lazy(() => import('./ConfirmBookingModal'));
const ReviewsModal = lazy(() => import('./ReviewsModal'));
const ShopModal = lazy(() => import('./ShopModal'));
const ProductDetailModal = lazy(() => import('./ProductDetailModal'));
const VouchersModal = lazy(() => import('./VouchersModal'));
// The gift-card and product shops now live on their own routes
// (/gift-cards, /shop) — openGiftCards/openProductShop navigate there.
const GiftCardOwnershipCue = lazy(() => import('../../wallet/GiftCardOwnershipCue'));
const AuthPromptModal = lazy(() => import('./AuthPromptModal'));
const ConsultationGateModal = lazy(() => import('./ConsultationGateModal'));
const FinishingGateModal = lazy(() => import('./FinishingGateModal'));
const PortfolioGalleryModal = lazy(() => import('./PortfolioGalleryModal'));

// Google Map component. withGoogleMap only, NOT withScriptjs: index.html
// already loads the Maps API globally (same key), and withScriptjs injected
// a SECOND copy of the script on every salon page - the "You have included
// the Google Maps JavaScript API multiple times" console error, plus a
// wasted fetch+parse competing with hero image decode at page open.
const MapWithAMarker = withGoogleMap((props) => (
  <GoogleMap
    defaultZoom={15}
    defaultCenter={{ lat: props.lat, lng: props.lng }}
    options={{
      disableDefaultUI: true,
      zoomControl: true,
      scrollwheel: false,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    }}
  >
    {props.isMarkerShown && <Marker position={{ lat: props.lat, lng: props.lng }} />}
  </GoogleMap>
));

// Mounts the map only when its container nears the viewport (the map lives
// at the bottom of the page, so most opens never pay for it at all), and
// only once the global Maps script - loaded async by index.html - is
// actually ready. Until both are true it holds a same-size placeholder so
// nothing jumps.
const LazyMap = ({ lat, lng }) => {
  const holderRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [mapsReady, setMapsReady] = useState(
    () => !!(window.google && window.google.maps)
  );

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px' } // start mounting a comfortable scroll before arrival
    );
    io.observe(el);
    // Fallback: some throttled WebViews starve IO callbacks. Mount after a
    // beat regardless - by then the page open is long past, so the map's
    // cost is off the critical path either way.
    const fallback = setTimeout(() => setInView(true), 8000);
    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    if (!inView || mapsReady) return undefined;
    const id = setInterval(() => {
      if (window.google && window.google.maps) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, [inView, mapsReady]);

  return (
    <div ref={holderRef} className="map-container">
      {inView && mapsReady ? (
        <MapWithAMarker
          containerElement={<div style={{ height: '200px', borderRadius: '12px', overflow: 'hidden' }} />}
          mapElement={<div style={{ height: '100%' }} />}
          lat={lat}
          lng={lng}
          isMarkerShown
        />
      ) : (
        <div style={{ height: '200px', borderRadius: '12px', background: '#eef0f2' }} aria-hidden />
      )}
    </div>
  );
};

// Pricing helpers — the salon-summary API returns per-stylist `servicePrice`
// inside `service.hairdressers[]`. A `servicePrice` of 0 means the stylist
// doesn't offer the service, so it must be excluded from the min calculation.
const computeFromPrice = (service) => {
  const prices = (service.hairdressers || [])
    .map(h => h.servicePrice)
    .filter(p => typeof p === 'number' && p > 0);
  return prices.length ? Math.min(...prices) : service.servicePrice;
};

// One empty team for every render, so the Times sheet doesn't see a new list
const NO_TEAM = [];

// What a booking call is sent about its discount code
const promoPayload = (p) => ({ code: p.code, codeId: p.codeId, amount: p.amount, lines: p.lines });

const getStylistPrice = (service, stylistId) => {
  if (!stylistId || !Array.isArray(service.hairdressers)) {
    return service.price;
  }
  const h = service.hairdressers.find(h => h._id === stylistId);
  return (h && typeof h.servicePrice === 'number' && h.servicePrice > 0)
    ? h.servicePrice
    : service.price;
};

// Service categories are stored PER STYLIST in the catalogue, so the same
// category recurs once per stylist who uses it — plus trailing-space / casing
// typos ("Styling" / "Styling " / "styling"). The customer booking page wants
// ONE tab per real category, with every stylist's services for that name
// merged under it. Canonicalise by trimmed+collapsed+lowercased key; the first
// occurrence (in backend category order) wins the displayed spelling.
//
// Both the tab list AND each service's `category` tag must run through the SAME
// canonical name, or the exact-match service filter would hide services whose
// raw category casing differs from the tab's (e.g. "styling" !== "Styling").
const canonicalCategoryDisplay = (name) =>
  (name || 'Other Services').trim().replace(/\s+/g, ' ');

const buildCategoryCanonicalizer = (serviceGroups) => {
  const byKey = {};        // normalised key -> first-seen display name
  const orderedNames = []; // display names in first-seen (backend) order
  (serviceGroups || []).forEach((group) => {
    const display = canonicalCategoryDisplay(group && group.categoryName);
    const key = display.toLowerCase();
    if (!(key in byKey)) {
      byKey[key] = display;
      orderedNames.push(display);
    }
  });
  // Map any raw category name to its canonical display (falls back to its own
  // cleaned form if the group was never seen — shouldn't happen, but safe).
  const canonical = (rawName) => {
    const display = canonicalCategoryDisplay(rawName);
    return byKey[display.toLowerCase()] || display;
  };
  return { canonical, orderedNames };
};

// Icons as SVG components
const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const ShareIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 16v4a2 2 0 002 2h12a2 2 0 002-2v-4M16 8l-4-4-4 4M12 4v12"
      stroke="#1a1a1a"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeartIcon = ({ filled = false }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill={filled ? "#ff4757" : "none"}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
      stroke={filled ? "#ff4757" : "#1a1a1a"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Toast notification component
const Toast = ({ message, type, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className={`toast-notification ${type} ${isVisible ? 'show' : ''}`}>
      <div className="toast-content">
        {type === 'success' && (
          <svg className="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        )}
        {type === 'error' && (
          <svg className="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        )}
        {type === 'heart' && (
          <svg className="toast-icon heart-icon" viewBox="0 0 24 24" fill="#ff4757" stroke="#ff4757" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        )}
        <span className="toast-message">{message}</span>
      </div>
    </div>
  );
};

const StarIcon = ({ filled = true }) => (
  <svg className={`star ${filled ? '' : 'empty'}`} viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// No photos yet: a plain, built-in backdrop. It used to be via.placeholder.com,
// a service that no longer answers, so salons without photos got the
// browser's broken image with its alt text in the corner (QA 2026-09-29).
const HERO_PLACEHOLDER = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="800" height="400" viewBox="0 0 800 400">' +
  '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#F1F0EE"/><stop offset="1" stop-color="#E4E2DE"/></linearGradient></defs>' +
  '<rect width="800" height="400" fill="url(#g)"/></svg>'
);

// Person avatar that swaps to an initials chip if the photo fails to load —
// a valid-looking URL can still 404 or time out, and a bare <img> then shows
// the browser's broken-image icon.
const AvatarWithFallback = ({ src, alt, className, initialsClassName, initials }) => {
  const [failed, setFailed] = useState(false);
  if (!failed && src) {
    return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
  }
  return <div className={initialsClassName}>{initials}</div>;
};

const SalonProfilePage = ({ dispatch }) => {
  const { salonId, salonSlug, hairdresserId } = useParams();
  const salonIdOrSlug = salonSlug || salonId;
  const history = useHistory();

  // /pro/:hairdresserId — a professional's own link, no salon in the path.
  // Everything below that needs a salon reference falls back to the one the
  // API returns, since the URL doesn't carry it.
  const isProRoute = !salonSlug && !salonId && !!hairdresserId;

  // Determine if this is a professional view or salon view
  const isProfessionalView = !!hairdresserId;

  // Only show the back button when the user arrived via in-app navigation (a
  // PUSH) — a direct load never gets one. This keeps a salon's clients, who
  // use the page as their booking link, from navigating out to the
  // marketplace and booking a competitor.
  //
  // A raw `history.action === 'PUSH'` check alone forgets the arrival: the
  // booking flow round-trips through full-page redirects (signup return,
  // Stripe cancel) and browser back/forward is a POP, so a client who
  // genuinely browsed in from the marketplace lost the button mid-flow.
  // Remember the in-app arrival in sessionStorage (per salon, for this tab's
  // lifetime): once they came from the marketplace, the button stays for the
  // whole session; a direct link in a fresh tab still never sets it.
  const [backNav] = useState(() => {
    const key = `salonBackAllowed:${salonIdOrSlug || hairdresserId || ''}`;
    const pushed = history.action === 'PUSH';
    let remembered = false;
    try {
      if (pushed) sessionStorage.setItem(key, '1');
      else remembered = sessionStorage.getItem(key) === '1';
    } catch (e) { /* storage unavailable — fall back to PUSH-only */ }
    return { pushed, remembered };
  });
  const canGoBack = backNav.pushed || backNav.remembered;

  // Whether to render the back arrow at all:
  //  - Salon page: only on in-app PUSH, per the rule above.
  //  - A professional shown UNDER a salon
  //    (/book/:salonSlug/professional/:id): always. The page openly belongs
  //    to that salon, so the arrow back up to it is expected even on a
  //    direct link. This doesn't reopen the competitor problem above - the
  //    destination is their own salon, not the marketplace.
  //  - An independent professional (/book/pro/:slug): never. That page IS
  //    the business; there is nothing above it to go back to.
  // Which of the two links a self-employed professional hands out is exactly
  // the "Part of {salon}" vs "Independent" choice in their Booking settings,
  // so the route is a faithful stand-in for that setting. (The flags
  // themselves - showOnSalonProfile / useOwnBookingLink - are still
  // write-only and absent from the public summary payload; if the backend
  // adds them, prefer reading the flag directly.)
  const showBackButton = isProfessionalView && !isProRoute ? true : canGoBack;
  
  // State for API data
  const [salonData, setSalonData] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // "Gift Cards" / the shop navigate to their permanent routes so the URL
  // always reflects where the customer is (shareable + SEO-friendly; the
  // browser back button returns to the salon page). The route wrappers render
  // the same full-screen shop components this page used to mount inline.
  //
  // The shop and gift cards belong to the SALON, so on /pro/:hairdresserId —
  // where the URL carries no salon — the base comes from the salon the API
  // returned. Until that arrives there is nothing to link to, and the buttons
  // aren't reachable anyway (the page is still loading).
  const shopBasePath = useMemo(() => {
    if (salonSlug) return `/book/${salonSlug}`;
    if (salonId) return `/salon/${salonId}`;
    if (salonData?.slug) return `/book/${salonData.slug}`;
    if (salonData?.salonId) return `/salon/${salonData.salonId}`;
    return null;
  }, [salonSlug, salonId, salonData]);

  const openGiftCards = useCallback(() => {
    if (!shopBasePath) return;
    history.push(`${shopBasePath}/gift-cards`);
  }, [history, shopBasePath]);
  const openProductShop = useCallback((productId) => {
    if (!shopBasePath) return;
    const q = typeof productId === 'string' && productId ? `?product=${productId}` : '';
    history.push(`${shopBasePath}/shop${q}`);
  }, [history, shopBasePath]);

  // State
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Touch/swipe state for hero carousel
  const [heroTouchStart, setHeroTouchStart] = useState(null);
  const [heroTouchEnd, setHeroTouchEnd] = useState(null);
  // Desktop drag-to-swipe. dragDelta drives a live transform offset so the
  // image tracks the cursor while held, rather than only jumping on release.
  const [heroDragStart, setHeroDragStart] = useState(null);
  const [heroDragDelta, setHeroDragDelta] = useState(0);
  /* Tap vs swipe. A gesture that barely moves is a tap and opens the
     full-screen viewer; anything past the threshold is a swipe and only moves
     the carousel. Shared by mouse and touch so both behave the same. */
  const heroTap = useRef({ x: 0, y: 0, moved: false });
  const HERO_TAP_SLOP = 8;
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProductCategory, setSelectedProductCategory] = useState('All');
  const [selectedServices, setSelectedServices] = useState([]);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('Services');
  const [showAllTeamMembers, setShowAllTeamMembers] = useState(false);
  
  // Booking flow modal states
  const [isStylistSelectionOpen, setIsStylistSelectionOpen] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const [isServiceDetailOpen, setIsServiceDetailOpen] = useState(false);
  const [isTimesModalOpen, setIsTimesModalOpen] = useState(false);
  const [isConfirmBookingOpen, setIsConfirmBookingOpen] = useState(false);
  // A time the assistant already agreed with the client, waiting to be pushed
  // through the normal checkout path (gates included).
  const [pendingAutoCheckout, setPendingAutoCheckout] = useState(null);
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
  const [isShopModalOpen, setIsShopModalOpen] = useState(false);
  const [isVouchersModalOpen, setIsVouchersModalOpen] = useState(false);
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedServiceForDetail, setSelectedServiceForDetail] = useState(null);
  const [selectedStylist, setSelectedStylist] = useState(null);
  const [servicesToFilterBy, setServicesToFilterBy] = useState([]); // Services to filter stylists by when modal opens
  const [bookingDetails, setBookingDetails] = useState({});
  
  // Share and Favorite state
  const [isFavorited, setIsFavorited] = useState(false);
  const [isAnimatingHeart, setIsAnimatingHeart] = useState(false);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });
  
  // Booking loader state
  const [bookingLoaderVisible, setBookingLoaderVisible] = useState(false);
  const [bookingLoaderState, setBookingLoaderState] = useState(LOADER_STATES.PREPARING);
  // True only when a deposit is actually charged; false for card-capture-only
  // (card held to secure the slot, no money taken) so the loader copy stays honest.
  const [bookingChargeNow, setBookingChargeNow] = useState(false);
  
  // Auth prompt modal state
  const [isAuthPromptOpen, setIsAuthPromptOpen] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  // Guest booking (GUEST-BOOKING-PLAN.md). The details sheet opens from the
  // auth prompt; the pending booking it will submit lives in
  // pendingBookingData exactly as for the sign-in path.
  // Which step the Confirm Booking sheet is showing. The sign-in choice, guest
  // details and OTP are STEPS inside that one sheet (GUEST-FLOW-UX-PLAN.md,
  // Amendment A), not separate modals stacked on top of it.
  const [confirmStep, setConfirmStep] = useState('summary');
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [guestSubmitError, setGuestSubmitError] = useState('');
  // Guest email-verification step (docs/guest-booking-be.md). The BE requires
  // a verified OTP before it creates anything, and the same step logs the
  // client in if the email turns out to be a claimed account.
  const [guestOtpVariant, setGuestOtpVariant] = useState('guest'); // | 'account_exists'
  const [guestOtpSentTo, setGuestOtpSentTo] = useState('');
  const [guestOtpSubmitting, setGuestOtpSubmitting] = useState(false);
  const [guestOtpError, setGuestOtpError] = useState('');
  // The details the OTP step will submit once the code is verified:
  // { details: {firstName,...,email,phoneNumber}, marketingConsent }.
  const [pendingGuest, setPendingGuest] = useState(null);
  // Who Rule 1 attributes this booking's client to, for the marketing consent
  // checkbox (GUEST-BOOKING-PLAN.md §5A). Fetched when the confirm sheet
  // opens; null while loading / on error (the checkbox then names the salon
  // and the BE records against its own resolution).
  const [consentContext, setConsentContext] = useState(null);
  const [authPromptContext, setAuthPromptContext] = useState('booking'); // 'booking' or 'favorite'

  // ---- Confirm sheet step helpers ----
  // Re-open the Confirm sheet directly at a step (used when a guest booking
  // create fails after the sheet has already closed: put the details step
  // back with the error rather than a bare alert).
  const openConfirmAt = useCallback((step) => {
    setConfirmStep(step);
    setIsConfirmBookingOpen(true);
  }, []);
  // Step navigation driven by the sheet's ‹ button. Clear step-local errors so
  // an old message doesn't greet the client when they come back.
  const handleConfirmStepChange = useCallback((next) => {
    setGuestSubmitError('');
    setGuestOtpError('');
    setConfirmStep(next);
  }, []);
  // Every fresh open starts at the summary. Close keeps the selection
  // (pendingBookingData / pendingGuest are NOT cleared — Amendment A §A3) but
  // never a mid-flow step.
  useEffect(() => {
    if (!isConfirmBookingOpen) setConfirmStep('summary');
  }, [isConfirmBookingOpen]);
  const [isConsultationGateOpen, setIsConsultationGateOpen] = useState(false);
  const [consultationGates, setConsultationGates] = useState([]);
  // Declaration ("Yes, I've had a consultation") is scoped to a
  // (stylistId, serviceIds) pair, not a global boolean. So:
  //  - Same stylist + same basket → declaration still applies, no re-prompt
  //    as the customer explores other slots.
  //  - Different stylist (any-stylist flow re-assigns per slot) OR different
  //    services → declaration doesn't apply → gate re-evaluates.
  // Respects SE-flexibility: stylists are independent businesses, so
  // declaration can't span them silently.
  const [consultationDeclaredFor, setConsultationDeclaredFor] = useState(null);
  const [consultationGateStage, setConsultationGateStage] = useState(null); // 'chooseSlot' | 'checkout'
  // Finishing-service gate fires when a service in the basket has
  // `requiresFinishing: true` and none of its allowed `finishingServiceIds`
  // are yet in the basket. We process one gate at a time, recompute after
  // each pick, and only continue to the slot picker when none remain.
  const [isFinishingGateOpen, setIsFinishingGateOpen] = useState(false);
  const [activeFinishingGate, setActiveFinishingGate] = useState(null);
  const [finishingGateStage, setFinishingGateStage] = useState(null); // 'chooseSlot' | 'checkout'
  
  // Portfolio gallery modal state
  const [isPortfolioGalleryOpen, setIsPortfolioGalleryOpen] = useState(false);
  const [portfolioGalleryInitialIndex, setPortfolioGalleryInitialIndex] = useState(0);
  
  // State for auto-continuing booking after login
  const [shouldContinueBooking, setShouldContinueBooking] = useState(false);
  const [restoredBookingData, setRestoredBookingData] = useState(null);
  
  // Fetch salon or staff data from API.
  //
  // Two speed rules, both learned the hard way on the Capacitor app:
  //  - Never await a fresh GPS fix here. getCurrentLocation() stalls up to a
  //    flat 5s in the iOS WebView (permission prompt pending/denied means the
  //    callback never fires), and it ran before the salon request even
  //    started — the whole "salon page takes 5s to open in the app" bug.
  //    getCachedLocation() is a synchronous read (24h cache, London
  //    fallback), same fix NewHomePage already uses, and the coords only
  //    feed the distance label anyway.
  //  - Stale-while-revalidate on the summary itself: a recently-viewed page
  //    renders instantly from cache while a background fetch silently
  //    refreshes it, instead of re-showing the skeleton on every visit.
  //
  // useLayoutEffect, not useEffect: the component mounts with loading=true,
  // and useEffect runs after the browser has already painted that frame — so
  // even a cache hit flashed the skeleton for a frame. Layout effects run
  // before paint, letting the cached branch commit real content into the
  // very first frame. The uncached path just kicks off an async fetch, so it
  // doesn't care which effect flavour it runs in.
  useLayoutEffect(() => {
    // Location for the distance calculation — synchronous, never blocks.
    const { latitude, longitude } = marketplaceService.getCachedLocation();

    const transform = (response) =>
      isProfessionalView
        // Pass hairdresserId from URL params since that's the correct ID for API calls
        ? transformStaffApiResponse(response, hairdresserId)
        : transformApiResponse(response);

    const applyData = (transformedData) => {
      setSalonData(transformedData);
      // Keep the visitor's category tab across a silent background refresh;
      // only reset when their current tab no longer exists (or first load).
      const categories = transformedData.serviceCategories || [];
      if (categories.length > 0) {
        setSelectedCategory((prev) => (prev && categories.includes(prev) ? prev : categories[0]));
      }
    };

    // Staff pages can be reached with or without a salon in the URL
    // (/book/:salonSlug/professional/:id vs /book/pro/:slug) — same person,
    // same payload, so key on the hairdresser alone.
    const cacheKey = isProfessionalView ? `staff:${hairdresserId}` : `salon:${salonIdOrSlug}`;

    const fetchData = async (isBackgroundRefresh) => {
      try {
        if (!isBackgroundRefresh) {
          setLoading(true);
          setBrandData(null);
        }
        setError(null);

        const response = isProfessionalView
          ? await marketplaceService.getStaffSummary({
              hairdresserId,
              salonId: salonIdOrSlug,
              latitude,
              longitude
            })
          : await marketplaceService.getSalonSummary({
              salonId: salonIdOrSlug,
              latitude,
              longitude
            });

        setSalonSummaryCache(cacheKey, response, { latitude, longitude });
        applyData(transform(response));
      } catch (err) {
        if (isBackgroundRefresh) {
          // The page is already showing good cached data — don't replace it
          // with an error screen over a failed silent refresh.
          console.warn('Background summary refresh failed:', err);
        } else {
          // Brand slugs share the permanent /book/:slug namespace with salons.
          // Only fall back after the salon lookup misses, so ordinary salon
          // pages pay no extra request.
          if (!isProfessionalView && salonIdOrSlug) {
            try {
              const brand = await sitesService.brandPage(salonIdOrSlug);
              if (brand && brand.brand) {
                setBrandData(brand);
                setError(null);
                return;
              }
            } catch (brandErr) { /* the original salon error is the useful one */ }
          }
          console.error('Error fetching data:', err);
          setError(err.message || 'Failed to load data');
        }
      } finally {
        setLoading(false);
      }
    };

    if (isProfessionalView ? hairdresserId : salonIdOrSlug) {
      const cachedResponse = getSalonSummaryCache(cacheKey, { latitude, longitude });
      if (cachedResponse) {
        applyData(transform(cachedResponse));
        setLoading(false);
        fetchData(true);
      } else {
        fetchData(false);
      }
    }
  }, [salonIdOrSlug, hairdresserId, isProfessionalView]);
  
  // Transform API response to match component's expected format
  const transformApiResponse = (response) => {
    const { salon, team, services, products, vouchers, stats } = response;
    // Collapse per-stylist / typo-variant category names into one tab each.
    const { canonical: canonicalCategory, orderedNames: orderedCategoryNames } =
      buildCategoryCanonicalizer(services);
    
    // Transform opening hours
    // Backend stores: { dayName: "Monday", sTime: "09:00", eTime: "17:00", isClose: false }
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const openingHours = {};
    
    if (salon.opening_hours && Array.isArray(salon.opening_hours) && salon.opening_hours.length > 0) {
      dayNames.forEach((day) => {
        // Find by dayName (string) - backend uses dayName like "Monday", "Tuesday", etc.
        const dayHours = salon.opening_hours.find(h => h.dayName === day);
        if (dayHours && !dayHours.isClose) {
          openingHours[day] = {
            open: true,
            from: dayHours.sTime || '9:00',
            to: dayHours.eTime || '18:00'
          };
        } else {
          openingHours[day] = { open: false };
        }
      });
    } else {
      // Default hours if not available
      dayNames.forEach(day => {
        openingHours[day] = day === 'Sunday' || day === 'Monday'
          ? { open: false }
          : { open: true, from: '9:00', to: '18:00' };
      });
    }

    // Flatten services with category info and collect featured services
    const flatServices = [];
    const featuredServices = [];
    services.forEach(category => {
      category.services.forEach(service => {
        const serviceObj = {
          id: service._id,
          name: service.serviceName,
          duration: `${service.serviceTime}m`,
          price: service.servicePrice,
          fromPrice: computeFromPrice(service),
          description: service.serviceDescription || '',
          serviceImages: service.serviceImages || [],
          category: canonicalCategory(category.categoryName),
          categoryId: category._id || category.categoryId || null,
          hairdressers: service.hairdressers || [],
          hairdresserIds: service.hairdresserIds || [],
          availableHairdressers: service.availableHairdressers || 0,
          depositPrice: service.depositPrice || 0,
          processingTime: service.processingTime || 0,
          finishingTime: service.finishingTime || 0,
          setupTime: service.setupTime || 0,
          cleanupTime: service.cleanupTime || 0,
          isFeaturedForSalon: service.isFeaturedForSalon || false,
          featuredOrderSalon: service.featuredOrderSalon || 0,
          requiresConsultation: service.requiresConsultation || false,
          consultationServiceId: service.consultationServiceId || null,
          consultationSameStylist: service.consultationSameStylist !== false,
          consultationMessage: service.consultationMessage || '',
          requiresFinishing: service.requiresFinishing || false,
          finishingServiceIds: service.finishingServiceIds || [],
          finishingSameStylist: service.finishingSameStylist !== false,
          finishingMessage: service.finishingMessage || '',
          // The service editor stores `finishingServiceIds` as `commonServiceId`
          // values (the canonical master-service ID shared across stylists),
          // not the salon-level `_id`. Surface both so the gate logic can
          // match against either.
          commonServiceId: service.commonServiceId || service._id
        };
        flatServices.push(serviceObj);

        // Collect featured services for salon view
        if (service.isFeaturedForSalon) {
          featuredServices.push({
            ...serviceObj,
            category: 'Featured' // Override category for featured display
          });
        }
      });
    });

    // Sort featured services by featuredOrderSalon
    featuredServices.sort((a, b) => (a.featuredOrderSalon || 0) - (b.featuredOrderSalon || 0));
    
    // Extract service categories - add "Featured" at the beginning if there are featured services
    const serviceCategories = [];
    if (featuredServices.length > 0) {
      serviceCategories.push('Featured');
    }
    serviceCategories.push(...orderedCategoryNames);
    if (serviceCategories.length === 0) {
      serviceCategories.push('All Services');
    }

    // Add featured services to the beginning of flatServices for display
    const allServices = featuredServices.length > 0
      ? [...featuredServices, ...flatServices]
      : flatServices;

    // Transform team members
    // Use member._id (User ID) for URL navigation to professional page
    // The backend getStaffSummary expects a User ID, not a Hairdresser document ID
    const transformedTeam = team.map(member => ({
      id: member._id,
      slug: member.slug || null,
      name: member.name,
      role: member.jobTitle || (member.isSalonOwner ? 'Owner' : 'Stylist'),
      rating: member.reviewSummary?.averageRating || 0,
      reviewCount: member.reviewSummary?.totalReviews || 0,
      image: member.userImage?.circleImage || member.userImage?.rectangleImage ||
             member.socialAvatar || 'https://via.placeholder.com/100',
      description: ''
    }));
    
    // Transform products
    const transformedProducts = products.map(product => ({
      id: product._id,
      name: product.productName,
      price: product.retailPrice,
      image: product.images?.[0]?.url || 'https://via.placeholder.com/200',
      images: product.images?.map(img => img.url) || [],
      description: product.description,
      category: product.category?.categoryName,
      brand: product.brand?.brandName
    }));
    
    // Get coordinates - handle both array format [lng, lat] and GeoJSON format {type, coordinates}
    let coordinates;
    if (salon.loc) {
      if (Array.isArray(salon.loc) && salon.loc.length >= 2) {
        // Simple array format: [longitude, latitude]
        coordinates = { lat: salon.loc[1], lng: salon.loc[0] };
      } else if (salon.loc.coordinates && Array.isArray(salon.loc.coordinates)) {
        // GeoJSON format: { type: "Point", coordinates: [longitude, latitude] }
        coordinates = { lat: salon.loc.coordinates[1], lng: salon.loc.coordinates[0] };
      } else {
        // Default coordinates
        coordinates = { lat: 51.5074, lng: -0.1278 };
      }
    } else {
      // Default coordinates
      coordinates = { lat: 51.5074, lng: -0.1278 };
    }
    
    return {
      id: salon._id,
      name: salon.salonName,
      rating: salon.reviewSummary?.averageRating || 0,
      reviewCount: salon.reviewSummary?.totalReviews || 0,
      description: salon.bio || '',
      // Hero images: main image first, then gallery images (avoiding duplicates)
      heroImages: (() => {
        const images = [];
        // Add main image first if it exists
        if (salon.image) {
          images.push(salon.image);
        }
        // Add gallery images, avoiding duplicates with main image
        if (salon.galleryImages && salon.galleryImages.length > 0) {
          const sortedGallery = salon.galleryImages
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(img => img.url)
            .filter(url => {
              // Skip if this URL matches the main image (compare filenames)
              if (!salon.image) return true;
              const mainFilename = salon.image.split('/').pop();
              const galleryFilename = url.split('/').pop();
              return mainFilename !== galleryFilename;
            });
          images.push(...sortedGallery);
        }
        return images.length > 0 ? images : [HERO_PLACEHOLDER];
      })(),
      // Gallery images are now included in heroImages, so this is empty for salon view
      galleryImages: [],
      address: salon.formattedAddress,
      // Sister locations, sent by the API when the owner has "Show my other
      // locations" on: [{ salonId, salonName, slug, area, address, image }].
      // `image` is the location's hero image URL. Empty otherwise.
      otherLocations: (() => {
        if (Array.isArray(salon.otherLocations)) return salon.otherLocations.filter((o) => o && o.slug);
        // Dev preview until the API sends the list: /book/<slug>?preview=locations
        if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === 'locations') {
          return [
            { salonId: 'preview-1', salonName: 'PB South', slug: 'pb-south', area: 'London', address: '14 Bermondsey Street, London SE1 3TQ', image: 'https://picsum.photos/seed/pb-south/900/600' },
            { salonId: 'preview-2', salonName: 'Fade & Blade Barbers', slug: 'fade-blade', area: 'Wilmslow', address: '3 Grove Street, Wilmslow SK9 1DR', image: 'https://picsum.photos/seed/fade-blade/900/600' },
          ];
        }
        return [];
      })(),
      coordinates,
      openingHours,
      serviceCategories,
      services: allServices,
      team: transformedTeam,
      products: transformedProducts,
      vouchers: {
        description: vouchers.length > 0
          ? 'Buy a voucher to use whenever you like for yourself or someone else.'
          : 'No vouchers available at this time.',
        benefits: 'Perfect for booking future services, trying something new, or gifting a little time to unwind.',
        items: vouchers
      },
      distance: salon.distance,
      stats,
      onlineBookingContactPhone: salon.onlineBookingContactPhone || '',
      onlineBookingContactEmail: salon.onlineBookingContactEmail || '',
      // Master switch. Absent means ON — no salon carries the field yet, and
      // reading absent as off would tell every salon it's closed.
      onlineBookingEnabled: salon.onlineBookingEnabled !== false,
      // Guest booking. Absent means OFF (opt-in at launch, GUEST-BOOKING-PLAN §2).
      allowGuestBooking: salon.allowGuestBooking === true,
      // OTP on guest checkout. Absent means REQUIRED — only an explicit false
      // (the salon's friction trade-off) skips the code step.
      guestBookingOtpRequired: salon.guestBookingOtpRequired !== false,
      // SEO fields
      slug: salon.slug || null,
      businessType: salon.businessType || 'hair_salon',
      administrativeAreaLevel1: salon.administrativeAreaLevel1 || '',
      administrativeAreaLevel2: salon.administrativeAreaLevel2 || '',
      neighborhood: salon.neighborhood || '',
      postalCode: salon.postalCode || '',
      countryLongName: salon.countryLongName || '',
      website: salon.website || '',
      rawOpeningHours: salon.opening_hours || []
    };
  };
  
  // Transform Staff API response to match component's expected format
  const transformStaffApiResponse = (response, urlHairdresserId) => {
    const { staff, salon, services, stats } = response;
    // Collapse per-stylist / typo-variant category names into one tab each.
    const { canonical: canonicalCategory, orderedNames: orderedCategoryNames } =
      buildCategoryCanonicalizer(services);
    
    // Flatten services with category info and collect featured services
    const flatServices = [];
    const featuredServices = [];
    services.forEach(category => {
      category.services.forEach(service => {
        const serviceObj = {
          id: service._id,
          name: service.serviceName,
          duration: `${service.serviceTime}m`,
          price: service.servicePrice,
          fromPrice: service.servicePrice,
          description: service.serviceDescription || '',
          serviceImages: service.serviceImages || [],
          category: canonicalCategory(category.categoryName),
          categoryId: category._id || category.categoryId || null,
          depositPrice: service.depositPrice || 0,
          processingTime: service.processingTime || 0,
          finishingTime: service.finishingTime || 0,
          setupTime: service.setupTime || 0,
          cleanupTime: service.cleanupTime || 0,
          isFeaturedForStaff: service.isFeaturedForStaff || false,
          featuredOrderStaff: service.featuredOrderStaff || 0,
          requiresConsultation: service.requiresConsultation || false,
          consultationServiceId: service.consultationServiceId || null,
          consultationSameStylist: service.consultationSameStylist !== false,
          consultationMessage: service.consultationMessage || '',
          requiresFinishing: service.requiresFinishing || false,
          finishingServiceIds: service.finishingServiceIds || [],
          finishingSameStylist: service.finishingSameStylist !== false,
          finishingMessage: service.finishingMessage || '',
          commonServiceId: service.commonServiceId || service._id,
          // The whole menu on a professional page is this stylist's by
          // definition. ServicesModal filters by hairdressers[] when a
          // stylist id is set (it is, mid-flow, once a slot assigns one);
          // without this stamp that filter matched nothing and the modal
          // showed "Fully Booked" over a non-empty basket.
          hairdressers: [{ _id: staff.userId || urlHairdresserId }]
        };
        flatServices.push(serviceObj);

        // Collect featured services for staff view (using isFeaturedForStaff)
        if (service.isFeaturedForStaff) {
          featuredServices.push({
            ...serviceObj,
            category: 'Featured' // Override category for featured display
          });
        }
      });
    });
    
    // Sort featured services by featuredOrderStaff
    featuredServices.sort((a, b) => (a.featuredOrderStaff || 0) - (b.featuredOrderStaff || 0));
    
    // Extract service categories - add "Featured" at the beginning if there are featured services
    const serviceCategories = [];
    if (featuredServices.length > 0) {
      serviceCategories.push('Featured');
    }
    serviceCategories.push(...orderedCategoryNames);
    if (serviceCategories.length === 0) {
      serviceCategories.push('All Services');
    }

    // Add featured services to the beginning of flatServices for display
    const allServices = featuredServices.length > 0
      ? [...featuredServices, ...flatServices]
      : flatServices;

    // Get staff member image - prefer circleImage, then rectangleImage
    const staffImage = staff.userImage?.circleImage || staff.userImage?.rectangleImage || '';
    
    // Build working hours from staff rota data (same format as salon opening hours)
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const openingHours = {};
    if (staff.workingHours && Array.isArray(staff.workingHours) && staff.workingHours.length > 0) {
      dayNames.forEach((day) => {
        const dayHours = staff.workingHours.find(h => h.dayName === day);
        if (dayHours && !dayHours.isClose) {
          openingHours[day] = {
            open: true,
            from: dayHours.sTime || '9:00',
            to: dayHours.eTime || '18:00'
          };
        } else {
          openingHours[day] = { open: false };
        }
      });
    } else {
      // Fallback to default hours if rota data is not available
      dayNames.forEach(day => {
        openingHours[day] = day === 'Sunday' || day === 'Monday'
          ? { open: false }
          : { open: true, from: '9:00', to: '18:00' };
      });
    }
    
    // Create a team member object for the staff (for TimesModal)
    // Use the resolved User ObjectId for booking API calls (handles both ObjectId and slug URLs)
    const resolvedUserId = staff.userId || urlHairdresserId;
    const staffAsStylist = {
      id: resolvedUserId,
      slug: staff.slug || null,
      name: staff.name,
      role: staff.jobTitle || (staff.isSalonOwner ? 'Owner' : 'Stylist'),
      rating: staff.reviewSummary?.averageRating || 0,
      reviewCount: staff.reviewSummary?.totalReviews || 0,
      image: staffImage || 'https://via.placeholder.com/100',
      description: ''
    };
    
    // Extract portfolio images
    const portfolioImages = (staff.portfolio || []).map(item => item.image).filter(Boolean);
    
    return {
      id: staff._id,
      name: staff.name,
      rating: staff.reviewSummary?.averageRating || 0,
      reviewCount: staff.reviewSummary?.totalReviews || 0,
      description: staff.bio || '', // Staff bio from API
      /* The hero used to be the avatar repeated, which is a portrait head-shot
         stretched across a wide banner — wrong crop, and it duplicated the
         picture already shown in the profile block below. A professional's
         portfolio is landscape work photography they have already uploaded, so
         it is the right source. Falls back to the avatar, then a placeholder,
         so someone with an empty portfolio is no worse off than before. */
      heroImages: portfolioImages.length
        ? portfolioImages
        : (staffImage ? [staffImage] : [HERO_PLACEHOLDER]),
      staffImage: staffImage, // Keep original staff image for profile display
      galleryImages: portfolioImages, // Portfolio images for gallery
      address: salon?.formattedAddress || '',
      coordinates: salon?.loc
        ? (Array.isArray(salon.loc) && salon.loc.length >= 2
          ? { lat: salon.loc[1], lng: salon.loc[0] }
          : salon.loc.coordinates && Array.isArray(salon.loc.coordinates)
            ? { lat: salon.loc.coordinates[1], lng: salon.loc.coordinates[0] }
            : { lat: 51.5074, lng: -0.1278 })
        : { lat: 51.5074, lng: -0.1278 },
      openingHours,
      serviceCategories,
      services: allServices,
      team: [staffAsStylist], // Include staff as the only team member for professional view
      products: [], // No products for staff view
      vouchers: {
        description: '',
        benefits: '',
        items: []
      },
      distance: salon?.distance,
      stats,
      // Staff-specific fields
      employmentType: staff.employmentType,
      employmentTypeId: staff.employmentTypeId,
      isSalonOwner: staff.isSalonOwner,
      jobTitle: staff.jobTitle, // Role/job title for display
      salonName: salon?.salonName,
      salonId: salon?._id,
      // Bookable only if BOTH switches are on — the salon's overrides the
      // professional's. Absent means on for both.
      onlineBookingEnabled:
        staff.onlineBookingEnabled !== false && salon?.onlineBookingEnabled !== false,
      // Guest booking: the professional's own setting if they carry one,
      // else the salon's. Absent means OFF (opt-in at launch).
      // Mirror the BE gate exactly (createOnlineBooking): a SELF-EMPLOYED pro
      // (employmentTypeId 2) is governed by their OWN flag; an owner or employed
      // stylist is governed by the SALON's. The old test was
      // `staff.allowGuestBooking !== undefined` and worked only while the API
      // omitted the field — now that the staff summary always returns a boolean,
      // an owner with no personal flag came back `false` and the salon fallback
      // never ran, so a salon with guest booking ON showed no guest option on
      // its pros' pages.
      allowGuestBooking: staff.employmentTypeId === 2
        ? staff.allowGuestBooking === true
        : salon?.allowGuestBooking === true,
      // OTP — same split as allowGuestBooking directly above: SE follows
      // their OWN flag, owner/employed follow the salon's.
      guestBookingOtpRequired: staff.employmentTypeId === 2
        ? staff.guestBookingOtpRequired !== false
        : salon?.guestBookingOtpRequired !== false,
      staffAsStylist, // Keep reference to the staff as stylist for initialStylist prop
      bio: staff.bio || '', // Staff bio
      portfolio: staff.portfolio || [], // Full portfolio data
      // Policy data for card capture logic
      cancellationPolicy: staff.cancellationPolicy || [],
      noShowStatus: staff.noShowStatus || 0,
      salonOwnerInfo: staff.salonOwnerInfo || null
    };
  };
  
  // Use transformed data or show loading/error state
  const data = salonData;

  // A tracked booking link (?src=instagram, ?src=qr): remember it for this
  // salon and count the visit, once the page knows the salon's id.
  const trackedSalonRef = isProfessionalView ? data?.salonId : data?.id;
  useEffect(() => {
    captureTrackedSource(trackedSalonRef);
  }, [trackedSalonRef]);

  // A discount code (?code=, or typed in the confirm sheet). Read from the
  // address on the first render, before the deep-link handlers clear it.
  const isSelfEmployedStylist = useCallback((id) => {
    if (!id || !data) return false;
    if (isProfessionalView) return Number(data.employmentTypeId) === 2 && Number(data.isSalonOwner) !== 1;
    for (const svc of (data.services || [])) {
      const h = (svc.hairdressers || []).find((x) => x && String(x._id) === String(id));
      if (h) return Number(h.employmentTypeId) === 2 && Number(h.isSalonOwner) !== 1;
    }
    return false;
  }, [data, isProfessionalView]);
  const bookingCode = useBookingCode({
    enabled: DISCOUNTS_VISIBLE,
    salonId: /^[0-9a-fA-F]{24}$/.test(String(trackedSalonRef || '')) ? String(trackedSalonRef) : null,
    isSelfEmployed: isSelfEmployedStylist,
  });
  const bookingCodeRef = useRef(bookingCode);
  bookingCodeRef.current = bookingCode;

  // Is this page actually taking online bookings?
  //
  // The server already REFUSES the booking (createOnlineBooking checks both the
  // salon's and the professional's switch), so money is safe either way — but
  // leaving the Book button, service rows and team cards live means a client
  // walks the whole flow and only gets told "no" at the end. Every entry point
  // has to be dead, not just the final step.
  //
  // Absent means ON: no salon or professional carries the field yet, and
  // reading absent as off would close every page on the platform.
  const bookingOpen = !data || data.onlineBookingEnabled !== false;

  // Guest booking gate. Offered whenever the salon/pro allows guest booking —
  // including bookings that need a deposit or a card on file. A verified guest
  // is a passwordless client: the card attaches to the booking (a Stripe
  // customer on their unclaimed record) and they run the same Stripe step as
  // anyone else, so there is no "pay at salon only" restriction. `allowed`
  // drives whether the guest UI exists at all; nothing is blocked by payment.
  const guestGate = useMemo(() => {
    const allowedBySalon = !!(data && data.allowGuestBooking);

    // On the SALON page the gate must follow the SELECTED stylist, not just the
    // salon: a self-employed pro is governed by their OWN flag (same rule as the
    // BE gate in createOnlineBooking). Without this the page offered guest
    // checkout for an SE who hadn't opted in, and the BE refused only AFTER the
    // client had filled in details and requested an OTP.
    // Professional view already resolves the right flag in its transform.
    if (!isProfessionalView) {
      const stylistId = pendingBookingData?.bookingDetails?.stylist?.id || bookingDetails?.stylist?.id;
      const services = pendingBookingData?.selectedServices || selectedServices;
      if (stylistId && Array.isArray(services)) {
        for (const svc of services) {
          const hd = Array.isArray(svc?.hairdressers)
            ? svc.hairdressers.find(h => h && h._id === stylistId)
            : null;
          if (hd && hd.employmentTypeId === 2) {
            // Self-employed: their own flag decides, salon flag is irrelevant.
            return { allowed: hd.allowGuestBooking === true, blockedByPayment: false };
          }
        }
      }
    }

    return { allowed: allowedBySalon, blockedByPayment: false };
  }, [data, isProfessionalView, pendingBookingData, bookingDetails, selectedServices]);

  // OTP gate — SAME split as the guest gate above: a booking with a
  // self-employed stylist follows THEIR guestBookingOtpRequired, everyone
  // else follows the salon's. Absent = required; only an explicit false
  // skips the code step. Mirrors the BE gate in createGuestBooking.
  const otpRequiredForBooking = useMemo(() => {
    if (!isProfessionalView) {
      const stylistId = pendingBookingData?.bookingDetails?.stylist?.id || bookingDetails?.stylist?.id;
      const services = pendingBookingData?.selectedServices || selectedServices;
      if (stylistId && Array.isArray(services)) {
        for (const svc of services) {
          const hd = Array.isArray(svc?.hairdressers)
            ? svc.hairdressers.find(h => h && h._id === stylistId)
            : null;
          if (hd && hd.employmentTypeId === 2) {
            return hd.guestBookingOtpRequired !== false;
          }
        }
      }
    }
    // Pro view resolves the right flag in its transform; salon page falls
    // back to the salon's setting for owner/employed stylists.
    return data?.guestBookingOtpRequired !== false;
  }, [data, isProfessionalView, pendingBookingData, bookingDetails, selectedServices]);
  
  // Check initial favorite status when data is loaded
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      const isLoggedIn = localStorage.getItem('accessToken');
      if (!isLoggedIn || !salonData) return;
      
      try {
        const params = isProfessionalView
          ? { stylistId: hairdresserId }
          : { salonId: salonData?.id || salonIdOrSlug };

        const status = await marketplaceService.checkFavoriteStatus(params);

        if (isProfessionalView && status.stylistFavorited !== undefined) {
          setIsFavorited(status.stylistFavorited);
        } else if (!isProfessionalView && status.salonFavorited !== undefined) {
          setIsFavorited(status.salonFavorited);
        }
      } catch (err) {
        console.warn('Could not check favorite status:', err);
      }
    };

    checkFavoriteStatus();
  }, [salonData, salonIdOrSlug, hairdresserId, isProfessionalView]);

  // Check for pending booking after returning from login or viewing stylist profile
  useEffect(() => {
    const checkPendingBooking = () => {
      const pendingBookingStr = localStorage.getItem('pendingBooking');
      
      if (pendingBookingStr && salonData) {
        try {
          const pendingBooking = JSON.parse(pendingBookingStr);

          // Discard stale pending bookings: prices, availability and the
          // chosen slot go off quickly, and a Confirm sheet popping up on a
          // salon visit days later reads as a glitch. Entries with no savedAt
          // predate the stamp (age unknown, service ids possibly stale) and
          // are treated as expired.
          const MAX_PENDING_AGE_MS = 60 * 60 * 1000;
          if (!pendingBooking.savedAt || (Date.now() - pendingBooking.savedAt) > MAX_PENDING_AGE_MS) {
            localStorage.removeItem('pendingBooking');
            return;
          }

          // Check if this pending booking is for this salon/professional.
          // Normalise both sides — an exact string compare silently fails on
          // trailing slashes or encoding differences, leaving the pending
          // booking to ambush the user on a later visit.
          const normalisePath = (p) => {
            try { p = decodeURIComponent(p || ''); } catch (e) { /* keep raw */ }
            return p.replace(/\/+$/, '');
          };
          const currentPath = normalisePath(window.location.pathname);
          if (normalisePath(pendingBooking.returnUrl) === currentPath) {
            // Clear the pending booking from storage
            localStorage.removeItem('pendingBooking');
            
            // Restore the booking state
            if (pendingBooking.selectedServices) {
              setSelectedServices(pendingBooking.selectedServices);
            }
            if (pendingBooking.bookingDetails) {
              setBookingDetails(pendingBooking.bookingDetails);
            }
            
            // If openServicesModal flag is set, open the services modal
            if (pendingBooking.openServicesModal) {
              // Small delay to ensure state is set before opening modal
              setTimeout(() => {
                setIsServicesModalOpen(true);
              }, 100);
            } else {
              // Show a toast to inform the user (for login flow)
              const accessToken = localStorage.getItem('accessToken');
              if (accessToken) {
                setToast({
                  isVisible: true,
                  message: 'Welcome back! Continuing with your booking...',
                  type: 'success'
                });
              }
              
              // Store the data and flag to continue booking
              setRestoredBookingData({
                services: pendingBooking.selectedServices || pendingBooking.services,
                stylists: pendingBooking.stylists,
                ...pendingBooking.bookingDetails
              });
              setShouldContinueBooking(true);
            }
          }
        } catch (err) {
          console.error('Error processing pending booking:', err);
          localStorage.removeItem('pendingBooking');
        }
      }
    };
    
    checkPendingBooking();
  }, [salonData]);

  // Handle returning from Stripe payment cancellation - reopen confirm modal
  useEffect(() => {
    const handlePaymentCancellation = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const showConfirm = urlParams.get('showConfirm');
      const bookingId = urlParams.get('bid');
      
      if (showConfirm === 'true' && bookingId && salonData) {
        try {
          // Fetch the booking data using the v2 API. The service expects
          // { bookingId } — passing the bare id requested /booking/undefined,
          // which the BE answers with HTTP 200 + an error envelope, and the
          // confirm sheet then opened EMPTY (0 services, £0.00).
          // Use the CLIENT booking endpoint, not the staff one. getBookingV2
          // hits /v2/booking/:id which requires hairdresserInfo — it can never
          // succeed for a client, and a guest has no localStorage session at all
          // (their booking-scoped token lives in sessionStorage). The call threw,
          // the catch swallowed it, and instead of their basket they got
          // "your booking is saved" — meaningless to a guest with no account.
          // This is the path taken when someone hits the browser back arrow on
          // the Stripe checkout page.
          let _guestToken = null;
          try { _guestToken = sessionStorage.getItem(`guestManageToken:${bookingId}`) || localStorage.getItem(`guestManageToken:${bookingId}`); } catch (e) { /* storage unavailable */ }
          const bookingData = await bookingService.getClientBookingById(bookingId, _guestToken);

          // Unwrap the v2 envelope: { statusCode, responseData: { data } }.
          const rd = bookingData?.data?.responseData || bookingData?.data;
          const booking = rd?.data || rd;

          // The client endpoint returns the array as `services`; the staff one
          // used `servicesBooked`. Accept either so this can't silently no-op.
          const _svcArr = Array.isArray(booking?.services)
            ? booking.services
            : (Array.isArray(booking?.servicesBooked) ? booking.servicesBooked : []);

          if (booking && _svcArr.length > 0) {
            // Transform services from booking to match the expected format
            const transformedServices = _svcArr.map(service => ({
              id: service.serviceId,
              name: service.serviceName,
              duration: `${service.serviceTime}m`,
              price: service.servicePrice,
              depositPrice: service.depositPrice || 0,
              processingTime: service.processingTime || 0,
              finishingTime: service.finishingTime || 0,
              setupTime: service.setupTime || 0,
              cleanupTime: service.cleanupTime || 0
            }));
            
            // Set the selected services
            setSelectedServices(transformedServices);
            
            // Set booking details. bookingSlot is [startMins, endMins] on the
            // wire — convert to "HH:MM" (timeToMinutes crashes on an array).
            const slotStart = Array.isArray(booking.bookingSlot) ? booking.bookingSlot[0] : null;
            setBookingDetails({
              date: booking.bookingDate ? new Date(booking.bookingDate) : new Date(),
              time: slotStart !== null
                ? `${String(Math.floor(slotStart / 60)).padStart(2, '0')}:${String(slotStart % 60).padStart(2, '0')}`
                : (typeof booking.bookingSlot === 'string' ? booking.bookingSlot : ''),
              stylist: {
                id: booking.hairdresserId,
                name: booking.hairdresserName || ''
              }
            });

            // The booking already EXISTS (created before the Stripe redirect).
            // Confirm must retry payment for it, not create a duplicate —
            // the duplicate can even be rejected because this booking holds
            // the slot.
            paymentRetryBookingIdRef.current = bookingId;

            // Open the confirm booking modal
            setTimeout(() => {
              setIsConfirmBookingOpen(true);
            }, 100);
            
            // Clean up the URL by removing query params
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            
            // Show a toast to inform the user
            setToast({
              isVisible: true,
              message: 'Payment was cancelled. Please try again.',
              type: 'error'
            });
          } else {
            // Booking couldn't be restored (error envelope or no services):
            // never open an empty confirm sheet. Clean the URL so a reload
            // doesn't retry, and tell the user what happened.
            console.error('[booking] Could not restore booking after payment cancel:', JSON.stringify(bookingData?.data)?.slice(0, 500));
            window.history.replaceState({}, document.title, window.location.pathname);
            setToast({
              isVisible: true,
              message: 'Payment was cancelled. Your booking is saved — you can find it in your bookings.',
              type: 'error'
            });
          }
        } catch (err) {
          console.error('Error fetching booking for payment cancellation:', err);
          setToast({
            isVisible: true,
            message: 'Could not restore your booking. Please try again.',
            type: 'error'
          });
        }
      }
    };
    
    handlePaymentCancellation();
  }, [salonData]);

  // Handle rebook service pre-selection from marketplace
  useEffect(() => {
    const handleRebookServicePreselection = () => {
      if (!salonData || !salonData.services) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const rebookServiceIds = urlParams.get('rebookServiceIds');
      
      if (!rebookServiceIds) return;
      
      // Parse the comma-separated service IDs
      const serviceIdsToSelect = rebookServiceIds.split(',').filter(id => id.trim());
      
      if (serviceIdsToSelect.length === 0) return;
      
      // Find matching services from the available services
      const matchingServices = [];
      
      salonData.services.forEach(service => {
        if (serviceIdsToSelect.includes(service.id)) {
          matchingServices.push({
            id: service.id,
            name: service.name,
            duration: service.duration,
            price: service.price,
            fromPrice: service.fromPrice,
            hairdressers: service.hairdressers || [],
            depositPrice: service.depositPrice || 0
          });
        }
      });
      
      if (matchingServices.length > 0) {
        // Pre-select the matching services
        setSelectedServices(matchingServices);
        
        // Show a toast to inform the user
        setToast({
          isVisible: true,
          message: `${matchingServices.length} service${matchingServices.length > 1 ? 's' : ''} pre-selected from your previous booking`,
          type: 'success'
        });
        
        // Clean up the URL by removing query params
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } else {
        // Services from previous booking are no longer available
        setToast({
          isVisible: true,
          message: 'Some services from your previous booking are no longer available',
          type: 'info'
        });
        
        // Clean up the URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };
    
    handleRebookServicePreselection();
  }, [salonData]);

  // Concierge handoff: the AI assistant's "Confirm <date> at <slot>" button
  // lands here with ?hairdresser=&services=&date=&slot=. Honour what the
  // assistant already agreed — preselect stylist + services and open the times
  // sheet — instead of dumping the client at the top of the page to re-pick
  // everything they just said. Mirrors the rebook deep-link above.
  useEffect(() => {
    if (!salonData || !salonData.services) return;
    const urlParams = new URLSearchParams(window.location.search);
    const cHairdresser = urlParams.get('hairdresser');
    const cServices = urlParams.get('services');
    // The assistant also sends the date and slot the client already agreed to.
    const cDate = urlParams.get('date');
    const cSlot = urlParams.get('slot');
    if (!cHairdresser || !cServices) return;

    const wanted = cServices.split(',').filter((id) => id.trim());
    // The assistant sends the per-stylist service doc `_id` (that's what the
    // BE booking facade books against), but the salon summary groups services
    // and may only surface that id under `hairdressers[].serviceId` — the
    // salon-level `id` is a different document. Match every id shape, same as
    // resolveServiceByLinkedId, or the handoff dead-ends on a "service no
    // longer available" toast for a service that's right there on the page.
    const wantedIdFor = (service) => {
      const ids = [service.id, service._id, service.commonServiceId];
      (Array.isArray(service.hairdressers) ? service.hairdressers : []).forEach((h) => {
        ids.push(h.serviceId, h._id, h.commonServiceId);
      });
      return ids.map((v) => (v == null ? null : String(v))).find((v) => v && wanted.includes(v)) || null;
    };
    // One basket row per wanted id — the summary can list the same service
    // under several groups (Featured + its own category), and matching all of
    // them doubled the basket (and the deposit) on handoff.
    const seenWanted = new Set();
    const matching = [];
    salonData.services.forEach((service) => {
      const hit = wantedIdFor(service);
      if (hit && !seenWanted.has(hit)) {
        seenWanted.add(hit);
        matching.push({
          id: service.id,
          name: service.name,
          duration: service.duration,
          price: service.price,
          fromPrice: service.fromPrice,
          hairdressers: service.hairdressers || [],
          depositPrice: service.depositPrice || 0
        });
      }
    });
    const stylist = (salonData.team || []).find(
      (m) => String(m.id) === cHairdresser || String(m.userId) === cHairdresser
    );

    // Clean the URL either way so a refresh doesn't re-fire the handoff.
    window.history.replaceState({}, document.title, window.location.pathname);

    if (!matching.length) {
      setToast({ isVisible: true, message: 'That service is no longer available — pick from the list below', type: 'info' });
      return;
    }
    setSelectedServices(matching);
    if (stylist) setSelectedStylist(stylist);

    // If the assistant already settled on a time, DON'T reopen the time picker.
    // The client had already confirmed the slot twice in chat; dropping them
    // back on the times sheet made it look as though nothing had happened —
    // and for a booking needing a deposit, they never reached Stripe at all.
    if (cDate && cSlot && stylist) {
      // Hand to the NORMAL checkout path rather than opening the confirm sheet
      // directly — handleCheckout runs the consultation and finishing gates,
      // and skipping those would let the assistant book a service that
      // requires a consultation first. Stashed as state because handleCheckout
      // is defined further down; a later effect picks this up.
      setPendingAutoCheckout({ stylist, date: cDate, time: cSlot, services: matching });
      return;
    }

    // No time agreed — the times sheet is the right next step.
    setIsTimesModalOpen(true);
  }, [salonData]);

  // Return from the sign-in flow during a product purchase: hand over to the
  // /shop route, whose wrapper consumes the draft and opens straight at
  // checkout with the (persisted) basket. Drafts normally return to the /shop
  // URL directly — this only catches ones saved against the salon page itself.
  useEffect(() => {
    if (!salonData) return;
    let draft = null;
    try { draft = JSON.parse(localStorage.getItem('pendingProductCheckout') || 'null'); } catch (e) { /* ignore */ }
    if (!draft || draft.returnUrl !== window.location.pathname) return;
    if (localStorage.getItem('accessToken')) {
      try {
        localStorage.setItem('pendingProductCheckout', JSON.stringify({ ...draft, returnUrl: `${shopBasePath}/shop` }));
      } catch (e) { /* ignore */ }
      history.replace(`${shopBasePath}/shop`);
    } else {
      localStorage.removeItem('pendingProductCheckout');
    }
  }, [salonData, history, shopBasePath]);

  // Same for a gift-card purchase opened from this page (modal, no route): on
  // return from sign-in/sign-up, re-open the gift-card shop so its own draft
  // restore (DRAFT_KEY 'pendingGiftCard') drops the buyer back on the details
  // step. We leave the draft in place — the modal's seed effect consumes it.
  useEffect(() => {
    if (!salonData) return;
    let draft = null;
    try { draft = JSON.parse(localStorage.getItem('pendingGiftCard') || 'null'); } catch (e) { /* ignore */ }
    if (draft && draft.returnUrl === window.location.pathname && localStorage.getItem('accessToken')) {
      openGiftCards();
    }
  }, [salonData, openGiftCards]);

  // Deep links shared by salon staff to a single service, professional or product:
  //   ?service=ID -> pre-select that service and scroll it into view
  //   ?staff=ID    -> open that professional's canonical booking page
  //   ?product=ID -> open the product shop straight to that product's detail
  // Service ids may be the salon `_id` or the canonical `commonServiceId`, so we
  // match against both. The /professional/:id attribution stays in the path; we
  // only strip the query so it doesn't re-fire.
  useEffect(() => {
    if (!salonData) return;
    const params = new URLSearchParams(window.location.search);
    const cleanQuery = () => window.history.replaceState({}, document.title, window.location.pathname);

    const productId = params.get('product');
    if (productId) {
      // Legacy share links pointed ?product= at the salon page — forward to
      // the canonical /shop route (new links point there directly).
      history.replace(`${shopBasePath}/shop?product=${encodeURIComponent(productId)}`);
      return;
    }

    const serviceId = params.get('service');
    const staffId = params.get('staff');

    if (staffId && !isProfessionalView && Array.isArray(salonData.team)) {
      const member = salonData.team.find((m) => [m.id, m.userId, m.slug].some((v) => v != null && String(v) === String(staffId)));
      if (member) {
        const ref = member.slug || member.id;
        const serviceQuery = serviceId ? `?service=${encodeURIComponent(serviceId)}` : '';
        history.replace(`${shopBasePath}/professional/${ref}${serviceQuery}`);
        return;
      }
      setToast({ isVisible: true, message: 'That team member is no longer available', type: 'info' });
      cleanQuery();
      return;
    }

    if (serviceId && Array.isArray(salonData.services)) {
      const match = salonData.services.find(
        (s) => [s.id, s._id, s.commonServiceId]
          .concat((s.hairdressers || []).reduce((ids, h) => ids.concat([h.serviceId, h._id, h.commonServiceId]), []))
          .some((v) => v != null && String(v) === String(serviceId))
      );
      if (match) {
        setSelectedServices([{
          id: match.id,
          name: match.name,
          duration: match.duration,
          price: match.price,
          fromPrice: match.fromPrice,
          hairdressers: match.hairdressers || [],
          depositPrice: match.depositPrice || 0,
        }]);
        setToast({ isVisible: true, message: `${match.name} selected`, type: 'success' });
        // Poll for the service card before scrolling — it can mount a beat after
        // salonData arrives (and after the mount "scroll to top" effects run).
        // Instant (not smooth) — this page's scroll setup no-ops smooth scrollIntoView.
        let attempts = 0;
        const scrollToCard = () => {
          const el = document.getElementById(`service-${match.id}`);
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'instant', block: 'center' });
            return;
          }
          if (attempts++ < 15) setTimeout(scrollToCard, 200);
        };
        setTimeout(scrollToCard, 300);
      } else {
        setToast({ isVisible: true, message: 'That service is no longer available', type: 'info' });
      }
      cleanQuery();
    }
  }, [salonData, history, shopBasePath, isProfessionalView]);

  // Hide the main navigation bar on mount and enable scrolling for this page
  // Also scroll to top when component mounts or route changes to fix issue where page opens mid-scroll
  useEffect(() => {
    // Scroll to top immediately when component mounts or route changes
    window.scrollTo(0, 0);
    
    document.body.classList.add('no-topbar', 'salon-profile-scrollable');
    return () => {
      document.body.classList.remove('no-topbar', 'salon-profile-scrollable');
    };
  }, [salonIdOrSlug, hairdresserId]);

  // URL canonicalization: redirect /salon/{objectId} to /book/{slug}
  useEffect(() => {
    if (salonData && salonData.slug && salonId && !salonSlug) {
      // Currently on /salon/{objectId} — redirect to /book/{slug}
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/salon/')) {
        const newPath = currentPath.replace(`/salon/${salonId}`, `/book/${salonData.slug}`);
        history.replace(newPath);
      }
    }
  }, [salonData, salonId, salonSlug, history]);

  // URL canonicalization for professional pages: redirect ObjectId to slug
  useEffect(() => {
    if (!salonData || !isProfessionalView || !hairdresserId) return;

    // Find the professional's slug (from staffAsStylist for professional view, or from team for salon view)
    const professionalSlug = salonData.staffAsStylist?.slug;
    if (!professionalSlug) return;

    // Only redirect if the URL currently uses an ObjectId (24-char hex), not already a slug
    const isObjectId = /^[a-f0-9]{24}$/.test(hairdresserId);
    if (!isObjectId) return;

    // On the pro's own link, canonicalise WITHIN /pro/ — bouncing them onto
    // the salon-scoped URL would undo the whole point of an independent link.
    if (isProRoute) {
      history.replace(`/book/pro/${professionalSlug}`);
      return;
    }

    const base = salonData.slug ? `/book/${salonData.slug}` : `/salon/${salonIdOrSlug}`;
    history.replace(`${base}/professional/${professionalSlug}`);
  }, [salonData, isProfessionalView, hairdresserId, salonIdOrSlug, isProRoute, history]);

  // Refs
  const contentRef = useRef(null);
  const servicesRef = useRef(null);
  const teamRef = useRef(null);
  const shopRef = useRef(null);
  const vouchersRef = useRef(null);
  const giftCardsRef = useRef(null);
  const infoRef = useRef(null);
  const locationsRef = useRef(null);
  
  // Check if vouchers are available for online sales
  const hasOnlineVouchers = data?.vouchers?.items?.length > 0;
  
  // Check if services are available for online booking
  const hasOnlineServices = data?.services?.length > 0;

  // Check if team members are available (only relevant when there are online services)
  const hasTeamMembers = data?.team?.length > 0;
  
  // Sister locations (owner has "Show my other locations" on)
  const hasOtherLocations = data?.otherLocations?.length > 0;

  // Can the user ACTUALLY make a booking right now?
  //
  // - Stylist view: the stylist is the team member, so having bookable
  //   services is enough.
  // - Salon view: a salon with services listed but no team members attached
  //   is unbookable (see e.g. Cannon Wharf Barbers - the Hair category has
  //   an entry but no stylist is set up to perform it). Without this check
  //   the Book button appeared active but opening the booking flow landed
  //   on a "no stylists available" dead end.
  //
  // Drives the Book button labels / disabled state. Does NOT gate the
  // Services section render - services still show so the user can browse.
  // bookingOpen is the master switch (salon, then professional). It gates every
  // Book CTA through this one flag rather than each button separately, so a new
  // CTA can't be added that quietly ignores it.
  const canBook = bookingOpen && (isProfessionalView
    ? hasOnlineServices
    : (hasOnlineServices && hasTeamMembers));

  // "Fully Booked" is the existing no-availability wording. It would be a lie
  // when the switch is simply off — the salon isn't full, it's closed to online
  // booking — so the two states read differently.
  const bookCtaLabel = (bookLabel) => {
    if (!bookingOpen) return 'Online booking unavailable';
    return canBook ? bookLabel : 'Fully Booked';
  };
  
  // Navigation items for sticky header - hide sections based on availability
  // Hide Services and Team if no online services are available
  // Hide Vouchers if no online vouchers are available
  const getNavItems = () => {
    if (isProfessionalView) {
      return hasOnlineServices ? ['Services', 'Info'] : ['Info'];
    }
    
    const items = ['Photos'];
    if (hasOnlineServices) {
      items.push('Services');
      if (hasTeamMembers) items.push('Team');
    }
    // TEMPORARILY COMMENTED OUT - Shop section
    // items.push('Shop');
    // "Gift Cards" routes to the standalone gift-card shop (not a scroll section).
    if (hasOnlineVouchers) items.push('Gift Cards');
    items.push('Info');
    if (hasOtherLocations) items.push('Locations');
    return items;
  };
  
  const navItems = getNavItems();

  // When we land on a salon or stylist (or switch between them on the same
  // SalonProfilePage instance), reset scroll + sticky-header state.
  // Without this the page can mount with window.scrollY inherited from the
  // previous route (React Router doesn't auto-scroll), and the sticky header
  // can carry over a stale `visible` state from a sibling view - producing a
  // flash where the sticky title + tabs are rendered over the top of the hero
  // on first paint, fixing itself once the user scrolls.
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsHeaderVisible(false);
    setActiveSection('Services');
  }, [salonIdOrSlug, hairdresserId]);

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = window.scrollY;
        // Hero is always at top now (mobile-like layout on all screen sizes)
        const heroHeight = 400;
        setIsHeaderVisible(scrollTop > heroHeight);
        
        // Update active section based on scroll position
        const sections = [
          { ref: servicesRef, name: 'Services' },
          { ref: teamRef, name: 'Team' },
          // TEMPORARILY COMMENTED OUT - Shop section
          // { ref: shopRef, name: 'Shop' },
          { ref: giftCardsRef, name: 'Gift Cards' },
          { ref: infoRef, name: 'Info' },
          { ref: locationsRef, name: 'Locations' },
        ];
        
        // At the very bottom of the page the last section may be too short
        // to ever reach the 150px line, so treat "scrolled to the end" as
        // that section being active.
        const atBottom = window.innerHeight + scrollTop >= document.documentElement.scrollHeight - 4;
        const lastMounted = [...sections].reverse().find((s) => s.ref.current);
        if (atBottom && lastMounted) {
          setActiveSection(lastMounted.name);
          return;
        }
        
        for (const section of sections) {
          if (section.ref.current) {
            const rect = section.ref.current.getBoundingClientRect();
            if (rect.top <= 150 && rect.bottom > 150) {
              setActiveSection(section.name);
              break;
            }
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Image carousel auto-play - only for professional view since salon view shows single hero image
  useEffect(() => {
    if (!data || !isProfessionalView) return;
    const images = [data.image];
    if (!images || images.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [data, isProfessionalView]);
  
  // Filter services by category
  const filteredServices = data?.services?.filter(
    (service) => service.category === selectedCategory
  ) || [];
  
  // Toggle service selection
  const toggleService = useCallback((serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  }, []);
  
  // Helper function to get available stylists based on selected services
  // Uses the same filtering logic as the StylistSelectionModal team prop
  const getAvailableStylists = useCallback((services = selectedServices) => {
    const team = data?.team || [];
    if (services.length === 0) {
      return team;
    }
    
    return team.filter(member => {
      // Check if this team member can perform all selected services
      return services.every(service => {
        // Get hairdresser IDs from both possible arrays
        let serviceHairdresserIds = [];
        
        // Check hairdressers array (array of objects with _id property)
        if (service.hairdressers && Array.isArray(service.hairdressers) && service.hairdressers.length > 0) {
          serviceHairdresserIds = service.hairdressers.map(h => h._id);
        }
        
        // If no hairdressers found, check hairdresserIds array (array of strings)
        if (serviceHairdresserIds.length === 0 && service.hairdresserIds && Array.isArray(service.hairdresserIds)) {
          serviceHairdresserIds = service.hairdresserIds;
        }
        
        // If no hairdressers defined for this service, assume all can do it
        if (serviceHairdresserIds.length === 0) {
          return true;
        }
        
        // Check if this member's ID is in the service's hairdressers list
        return serviceHairdresserIds.includes(member.id);
      });
    });
  }, [data?.team, selectedServices]);
  // The same list for the Times sheet until the team or basket changes: a new
  // array on every render made Times reset (refetch, chosen time cleared)
  // whenever this page re-rendered, e.g. when the finishing step opened.
  const timesTeam = useMemo(() => getAvailableStylists(), [getAvailableStylists]);
  
  // Helper function to open stylist selection modal or auto-select if only 1 stylist available
  const openStylistSelectionOrAutoSelect = useCallback((services = selectedServices) => {
    if (!bookingOpen) return;
    const availableStylists = getAvailableStylists(services);
    
    // Store the services to filter by for the modal
    setServicesToFilterBy(services);
    
    if (availableStylists.length === 0) {
      // No stylists available for this combination of services
      // Show the stylist selection modal which will display an appropriate message
      setIsStylistSelectionOpen(true);
    } else if (availableStylists.length === 1) {
      // Auto-select the only available stylist and skip to services modal
      setSelectedStylist(availableStylists[0]);
      setIsServicesModalOpen(true);
    } else {
      // Show stylist selection modal for user to choose
      setIsStylistSelectionOpen(true);
    }
  }, [getAvailableStylists, selectedServices]);
  
  // Handle service card click - pre-select service and open modal
  const handleServiceCardClick = useCallback((service) => {
    if (!bookingOpen) return;
    // Pre-select the service if not already selected
    const isAlreadySelected = selectedServices.some(s => s.id === service.id);
    const updatedServices = isAlreadySelected
      ? selectedServices
      : [...selectedServices, service];
    
    if (!isAlreadySelected) {
      setSelectedServices(updatedServices);
    }
    
    // Open the services modal
    if (isProfessionalView) {
      // For professional view, go directly to services modal
      setIsServicesModalOpen(true);
    } else {
      // For salon view, show stylist selection or auto-select if only 1 stylist
      // Pass the updated services array to ensure the newly selected service is included
      openStylistSelectionOrAutoSelect(updatedServices);
    }
  }, [isProfessionalView, selectedServices, openStylistSelectionOrAutoSelect]);
  
  // Toggle service selection for booking flow (uses full service object)
  const toggleServiceForBooking = useCallback((service) => {
    setSelectedServices((prev) => {
      const isSelected = prev.some(s => s.id === service.id);
      if (isSelected) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
  }, []);
  
  // Handle service click to show detail popup
  const handleServiceClick = useCallback((service) => {
    if (!bookingOpen) return;
    setSelectedServiceForDetail(service);
    setIsServiceDetailOpen(true);
  }, []);
  
  // Handle add to booking from service detail
  const handleAddToBooking = useCallback((service) => {
    // Calculate the updated services list synchronously
    const isAlreadySelected = selectedServices.some(s => s.id === service.id);
    const updatedServices = isAlreadySelected
      ? selectedServices.filter(s => s.id !== service.id)
      : [...selectedServices, service];
    
    // Update state
    setSelectedServices(updatedServices);
    setIsServiceDetailOpen(false);
    
    // Open the services modal with the service selected
    if (isProfessionalView) {
      // For professional view, go directly to services modal
      setIsServicesModalOpen(true);
    } else {
      // For salon view, show stylist selection or auto-select if only 1 stylist
      // Pass the updated services array to ensure the newly selected service is included
      openStylistSelectionOrAutoSelect(updatedServices);
    }
  }, [selectedServices, isProfessionalView, openStylistSelectionOrAutoSelect]);
  
  // Compute which (service, stylist) combos require a first-visit consultation.
  // Per-stylist data on `service.hairdressers` is the source of truth; falls back
  // to top-level service fields in case the marketplace API aggregates them.
  const computeConsultationGates = useCallback((services, stylist) => {
    if (!Array.isArray(services) || services.length === 0) return [];
    const gates = [];
    services.forEach((svc) => {
      let requires = false;
      let consultationServiceId = null;
      let consultationSameStylist = true;
      let consultationMessage = '';
      // When a stylist is selected, the per-stylist flag is the only
      // authoritative answer — the service-level rollup is "any stylist's
      // record" picked by $first in the salon aggregation, so falling back
      // to it would gate even stylists who'd toggled consultation off.
      if (stylist && Array.isArray(svc.hairdressers)) {
        const hd = svc.hairdressers.find((h) => h._id === stylist.id);
        if (hd && hd.requiresConsultation) {
          requires = true;
          consultationServiceId = hd.consultationServiceId || null;
          consultationSameStylist = hd.consultationSameStylist !== false;
          // Per-stylist message is authoritative when present; fall back to
          // the service-level field so the client still sees a message even
          // if the public salon endpoint isn't echoing the per-stylist
          // consultationMessage through svc.hairdressers[].
          consultationMessage = hd.consultationMessage || svc.consultationMessage || '';
        }
      } else if (svc.requiresConsultation) {
        // No stylist picked yet (any-stylist flow) — fall back to the
        // service-level field so the gate still surfaces in the catalog.
        requires = true;
        consultationServiceId = svc.consultationServiceId || null;
        consultationSameStylist = svc.consultationSameStylist !== false;
        consultationMessage = svc.consultationMessage || '';
      }
      if (requires) {
        gates.push({ service: svc, stylist, consultationServiceId, consultationSameStylist, consultationMessage });
      }
    });
    return gates;
  }, []);

  // Resolve a service from the services list by a "linked service" id —
  // used by BOTH the consultation gate (consultationServiceId) and the
  // finishing gate (finishingServiceIds[]). The editor stores those as a
  // `commonServiceId` (master ID), but depending on what the salon-summary
  // endpoint echoes a service may surface that id under `commonServiceId`,
  // the salon-level `id`/`_id`, or only on a per-stylist `hairdressers[]`
  // record. Match against all of them so resolution works regardless of
  // which ID shape the backend sends.
  const resolveServiceByLinkedId = useCallback((cidRaw) => {
    if (!cidRaw) return null;
    // Normalise the reference to a comparable string. It can arrive as a
    // plain string, a Mongo ObjectId-like object, or a fully populated
    // service object ({ _id, serviceName, ... }) depending on how the
    // salon-summary endpoint serialises it — so plain === would silently miss.
    const idOf = (v) => {
      if (v == null) return null;
      if (typeof v === 'string') return v.trim();
      if (typeof v === 'object') return String(v._id || v.id || v.commonServiceId || v.serviceId || v);
      return String(v);
    };
    const cid = idOf(cidRaw);
    if (!cid) return null;
    const eq = (v) => v != null && idOf(v) === cid;
    return (data?.services || []).find((s) =>
      eq(s.commonServiceId) ||
      eq(s.id) ||
      eq(s._id) ||
      (Array.isArray(s.hairdressers) && s.hairdressers.some((h) =>
        eq(h.serviceId) || eq(h._id) || eq(h.commonServiceId)))
    ) || null;
  }, [data]);

  // Look up the linked consultation service name on each gate so the modal copy
  // can name it. Returns the same shape with `consultationServiceName` and the
  // resolved `consultationService` object attached.
  const enrichGatesWithNames = useCallback((gates) => {
    return gates.map((g) => {
      const consultSvc = resolveServiceByLinkedId(g.consultationServiceId);
      return { ...g, consultationServiceName: consultSvc?.name || null, consultationService: consultSvc || null };
    });
  }, [resolveServiceByLinkedId]);

  // Compute which (service, stylist) combos require a finishing service that
  // hasn't been added to the basket yet. Mirrors computeConsultationGates —
  // per-stylist override wins, then service-level fields. A gate is only
  // raised if NONE of the allowed `finishingServiceIds` are already in the
  // basket — adding any one of them satisfies the requirement.
  const computeFinishingGates = useCallback((services, stylist) => {
    if (!Array.isArray(services) || services.length === 0) return [];
    // Match by EITHER `commonServiceId` (the canonical master ID the service
    // editor stores in finishingServiceIds) OR `id` (salon-level _id) so we
    // resolve regardless of which the backend echoes.
    const selectedKeys = new Set();
    services.forEach((s) => {
      if (s.commonServiceId) selectedKeys.add(s.commonServiceId);
      if (s.id) selectedKeys.add(s.id);
    });
    const gates = [];
    services.forEach((svc) => {
      let requires = false;
      let finishingServiceIds = [];
      let finishingMessage = '';
      // Same per-stylist-vs-service-rollup rule as the consultation gate:
      // once a stylist is picked, only their flag matters. The service-level
      // field is the $first-pick from the salon aggregation, not a rollup.
      if (stylist && Array.isArray(svc.hairdressers)) {
        const hd = svc.hairdressers.find((h) => h._id === stylist.id);
        if (hd && hd.requiresFinishing) {
          requires = true;
          // Per-stylist values are authoritative when present; fall back to
          // the service-level fields so the modal still gets a list to show
          // even if the public salon endpoint isn't echoing the per-stylist
          // finishingServiceIds[] / finishingMessage through svc.hairdressers[].
          const perStylistIds = Array.isArray(hd.finishingServiceIds) ? hd.finishingServiceIds : [];
          const svcLevelIds = Array.isArray(svc.finishingServiceIds) ? svc.finishingServiceIds : [];
          finishingServiceIds = perStylistIds.length > 0 ? perStylistIds : svcLevelIds;
          finishingMessage = hd.finishingMessage || svc.finishingMessage || '';
        }
      } else if (svc.requiresFinishing) {
        requires = true;
        finishingServiceIds = Array.isArray(svc.finishingServiceIds) ? svc.finishingServiceIds : [];
        finishingMessage = svc.finishingMessage || '';
      }
      if (!requires) return;
      // Already satisfied if any allowed finishing service id matches a
      // commonServiceId or salon-level id already in the basket.
      const alreadySatisfied = finishingServiceIds.some((id) => selectedKeys.has(id));
      if (alreadySatisfied) return;
      // Hydrate finishing service objects from the salon services list so the
      // modal can show name + price. Uses the same robust resolver as the
      // consultation gate (matches commonServiceId / id / _id / per-stylist
      // hairdressers[] ids, with type normalisation) so a finishing service
      // resolves regardless of which ID shape the backend echoes.
      const finishingServices = finishingServiceIds
        .map((id) => resolveServiceByLinkedId(id))
        .filter(Boolean);
      if (finishingServiceIds.length > 0 && finishingServices.length === 0) {
        // Service requires a finishing service but none of its linked ids
        // resolved to a service the salon-summary endpoint exposes — the same
        // ID mismatch / stale-reference issue as the consultation gate.
        // Resolves once the backend reconciles finishingServiceIds; logged so
        // affected salons are easy to spot.
        // eslint-disable-next-line no-console
        console.warn(
          `[FinishingGate] "${svc.name}" links finishing services (${finishingServiceIds.join(', ')}) not exposed by the booking endpoint — ID mismatch / stale references. Needs the backend ID-consistency fix (same as consultations).`
        );
      }
      gates.push({ service: svc, stylist, finishingServices, finishingMessage });
    });
    return gates;
  }, [data, resolveServiceByLinkedId]);

  // Handle choose slot - open times modal (with consultation gate check,
  // then finishing-service gate check). Consultation runs first because it's
  // a yes/no acknowledgement; finishing gate adds a new service to the basket
  // and would otherwise re-trigger the consultation check.
  // Returns true when the customer has already declared "Yes, I've had a
  // consultation" for the SAME stylist and the SAME basket. Either changing
  // invalidates the declaration so the gate re-evaluates.
  const declarationApplies = useCallback((declaredFor, stylist, services) => {
    if (!declaredFor) return false;
    const stylistId = stylist?.id || stylist?._id || null;
    if (declaredFor.stylistId !== stylistId) return false;
    const currentIds = (services || []).map(
      (s) => String(s._id || s.id || s.commonServiceId)
    ).sort();
    if (declaredFor.serviceIds.length !== currentIds.length) return false;
    return declaredFor.serviceIds.every((id, i) => id === currentIds[i]);
  }, []);

  const handleChooseSlot = useCallback(() => {
    const activeStylist = selectedStylist || (isProfessionalView ? data?.staffAsStylist : null);

    // Any-stylist flow (no specific stylist picked yet): defer ALL gate
    // checks to slot-pick. Each slot in TimesModal carries its own assigned
    // staffId; `handleCheckout` then evaluates the gate against that real
    // assignment. The salon-level svc.requiresConsultation /
    // svc.requiresFinishing rollups are just $first-from-aggregation, not
    // true salon-wide flags — gating on them here would friction-prompt
    // customers about requirements that may not apply to the stylist who
    // actually ends up serving them. SE-flexibility means each stylist's
    // setting wins, evaluated when the assignment is actually known.
    if (!activeStylist) {
      setIsServicesModalOpen(false);
      setIsTimesModalOpen(true);
      return;
    }

    // Stylist-picked flow: gate upfront against that stylist's per-service
    // requirements (existing behaviour). Honour a prior declaration for the
    // same (stylist, services) pair to avoid re-prompting on a fresh attempt.
    const triggered = computeConsultationGates(selectedServices, activeStylist);
    if (triggered.length > 0 && !declarationApplies(consultationDeclaredFor, activeStylist, selectedServices)) {
      setConsultationGates(enrichGatesWithNames(triggered));
      setConsultationGateStage('chooseSlot');
      setIsConsultationGateOpen(true);
      return;
    }
    const finishingGates = computeFinishingGates(selectedServices, activeStylist);
    if (finishingGates.length > 0) {
      setActiveFinishingGate(finishingGates[0]);
      setFinishingGateStage('chooseSlot');
      setIsFinishingGateOpen(true);
      return;
    }
    setIsServicesModalOpen(false);
    setIsTimesModalOpen(true);
  }, [selectedServices, selectedStylist, isProfessionalView, data, consultationDeclaredFor, declarationApplies, computeConsultationGates, computeFinishingGates, enrichGatesWithNames]);

  // Handle checkout — fires on slot pick from TimesModal with the assigned
  // stylist on `details.stylist`. This is the PRIMARY gate point for the
  // any-stylist flow (handleChooseSlot defers to here), and a safety net
  // for the stylist-picked flow. Declaration is checked against the
  // (stylist, services) pair so changing either re-triggers the gate.
  const handleCheckout = useCallback((details) => {
    setBookingDetails(details);
    const triggered = computeConsultationGates(selectedServices, details?.stylist);
    if (triggered.length > 0 && !declarationApplies(consultationDeclaredFor, details?.stylist, selectedServices)) {
      setConsultationGates(enrichGatesWithNames(triggered));
      setConsultationGateStage('checkout');
      setIsConsultationGateOpen(true);
      return;
    }
    const finishingGates = computeFinishingGates(selectedServices, details?.stylist);
    if (finishingGates.length > 0) {
      setActiveFinishingGate(finishingGates[0]);
      setFinishingGateStage('checkout');
      setIsFinishingGateOpen(true);
      return;
    }
    setIsTimesModalOpen(false);
    setIsConfirmBookingOpen(true);
  }, [selectedServices, consultationDeclaredFor, declarationApplies, computeConsultationGates, computeFinishingGates, enrichGatesWithNames]);

  // Consume an assistant handoff that already carries a time: straight to
  // checkout, gates and all. Placed after handleCheckout so it can call it.
  useEffect(() => {
    if (!pendingAutoCheckout) return;
    const details = pendingAutoCheckout;
    setPendingAutoCheckout(null);
    handleCheckout(details);
  }, [pendingAutoCheckout, handleCheckout]);

  const handleConsultationYes = useCallback(() => {
    // Record what the customer just declared FOR — the specific stylist
    // and the current basket. Used by declarationApplies on subsequent
    // gate evals so the customer isn't re-prompted for the same situation
    // but IS re-prompted if they change stylist or services.
    const declaredStylist = consultationGates[0]?.stylist;
    const stylistId = declaredStylist?.id || declaredStylist?._id || null;
    const serviceIds = (selectedServices || []).map(
      (s) => String(s._id || s.id || s.commonServiceId)
    ).sort();
    setConsultationDeclaredFor({ stylistId, serviceIds });
    setIsConsultationGateOpen(false);
    if (consultationGateStage === 'chooseSlot') {
      // gate fired before slot picking → continue into times modal
      setIsServicesModalOpen(false);
      setIsTimesModalOpen(true);
    } else {
      // gate fired at checkout (primary for any-stylist, safety net for
      // stylist-picked) → continue into confirm modal
      setIsTimesModalOpen(false);
      setIsConfirmBookingOpen(true);
    }
    setConsultationGateStage(null);
  }, [consultationGateStage, consultationGates, selectedServices]);

  const handleConsultationNo = useCallback(() => {
    const first = consultationGates[0];
    setIsConsultationGateOpen(false);
    if (!first) return;

    // Prefer the pre-resolved object from enrichGatesWithNames; fall back to
    // the broadened multi-key resolver in case it was missed.
    const consultationService = first.consultationService
      || resolveServiceByLinkedId(first.consultationServiceId);

    setConsultationGates([]);
    setConsultationGateStage(null);
    // No need for a "don't re-gate" flag here: the basket is being swapped
    // to ONLY the consultation service below, which itself doesn't require
    // a consultation. With scoped declaration, the swap changes the basket
    // anyway, so any prior declaration is naturally invalidated.

    if (consultationService) {
      // Swap the basket to ONLY the consultation, then drop the client back on
      // the services list so they can see the swap and proceed via the normal
      // Choose Slot path. Switch the active category tab to the consultation's
      // so its row reads as selected.
      setSelectedServices([consultationService]);
      if (consultationService.category) setSelectedCategory(consultationService.category);
      // Stylist is intentionally NOT cleared. `selectedStylist` already
      // reflects how the client entered: a chosen stylist stays locked to that
      // stylist; "any stylist" stays null; a professional-URL entry is
      // re-derived by handleChooseSlot. This is the "always lock to the chosen
      // stylist" behaviour the salon needs (entry context wins).
      setIsTimesModalOpen(false);
      setIsConfirmBookingOpen(false);
      setIsServicesModalOpen(true);
      return;
    }

    // Fallback (now rare): couldn't resolve the linked consultation service —
    // the salon-summary endpoint isn't echoing a matchable id for it. Log the
    // ids so the mismatch is visible in the live build, then drop the user on
    // the services list with a toast. Stylist still preserved (see above).
    // Resolution failed because the gated service's `consultationServiceId`
    // points at a service that's no longer in the salon's list — almost
    // always a STALE REFERENCE (the linked consultation was deleted and
    // recreated with a new id). Re-saving the service's consultation setting
    // in the editor stores the current id and fixes it. Logged concisely so
    // affected salons are easy to spot without flooding the console.
    // eslint-disable-next-line no-console
    console.warn(
      `[ConsultationGate] "${first.service?.name}" links a consultation service (${first.consultationServiceId}) that no longer exists in this salon — stale reference. Re-save the service's consultation setting to fix.`
    );
    setIsTimesModalOpen(false);
    setIsConfirmBookingOpen(false);
    setIsServicesModalOpen(true);
    setToast({
      isVisible: true,
      message: 'Please add a consultation service before continuing.',
      type: 'error',
    });
  }, [consultationGates, data, resolveServiceByLinkedId]);

  // Finishing-gate: close without choosing — back the user out of the flow
  // and leave them on the services modal so they can change selection.
  const handleFinishingClose = useCallback(() => {
    setIsFinishingGateOpen(false);
    setActiveFinishingGate(null);
    setFinishingGateStage(null);
  }, []);

  // Finishing-gate: user picked a finishing service from the chooser.
  // Add it to the basket, then recompute remaining gates. If another
  // service in the basket still needs a finishing, fire the next gate.
  // Otherwise close the modal and continue to the original target step.
  const handleFinishingPick = useCallback((finishingService) => {
    if (!finishingService) return;
    const stage = finishingGateStage;
    const activeStylist = selectedStylist || (isProfessionalView ? data?.staffAsStylist : null);
    setSelectedServices((prev) => {
      // Avoid double-adding if the user clicks twice quickly.
      if (prev.some((s) => s.id === finishingService.id)) return prev;
      const next = [...prev, finishingService];
      // Recompute gates with the new basket; fire the next one if any.
      const remaining = computeFinishingGates(next, stage === 'checkout' ? bookingDetails?.stylist : activeStylist);
      if (remaining.length > 0) {
        setActiveFinishingGate(remaining[0]);
      } else {
        setIsFinishingGateOpen(false);
        setActiveFinishingGate(null);
        setFinishingGateStage(null);
        if (stage === 'chooseSlot') {
          setIsServicesModalOpen(false);
          setIsTimesModalOpen(true);
        } else if (stage === 'checkout') {
          setIsTimesModalOpen(false);
          setIsConfirmBookingOpen(true);
        }
      }
      return next;
    });
  }, [finishingGateStage, selectedStylist, isProfessionalView, data, bookingDetails, computeFinishingGates]);

  // Handle confirm booking
  const bookingSubmittingRef = React.useRef(false);
  // Set when returning from a cancelled Stripe checkout: the booking already
  // exists, so Confirm retries payment for this id instead of creating a
  // duplicate. Cleared when the confirm sheet is dismissed.
  const paymentRetryBookingIdRef = React.useRef(null);

  // Where a completed booking lands. Built in one place because it was
  // previously duplicated at four call sites (payment-failed, no-session,
  // payment-threw, and no-payment-needed) and they must not drift apart.
  //
  // On /pro/:hairdresserId the success page stays on the pro's own link — the
  // client booked through their brand, so bouncing them to the salon's URL at
  // the moment of confirmation would be jarring and would leak the salon slug
  // into a link the pro shares.
  const buildBookingSuccessUrl = useCallback((data, selectedStylistId, bookingId) => {
    const query = `success?bid=${bookingId}`;

    if (isProRoute) {
      return `/book/pro/${hairdresserId}/${query}`;
    }

    const targetSalonRef = isProfessionalView ? data?.salonId : (data?.slug || salonIdOrSlug);
    const targetBase = data?.slug && !isProfessionalView ? '/book' : '/salon';
    const targetHairdresserId = isProfessionalView ? hairdresserId : selectedStylistId;
    return `${targetBase}/${targetSalonRef}/professional/${targetHairdresserId}/${query}`;
  }, [isProRoute, isProfessionalView, hairdresserId, salonIdOrSlug]);

  const handleConfirmBooking = useCallback(async (bookingData) => {
    // Prevent double-submit — if a booking request is already in flight, ignore
    if (bookingSubmittingRef.current) return;
    bookingSubmittingRef.current = true;

    console.log('Booking confirmed:', bookingData);

    // Guest booking carries the guest's details on bookingData.guest and
    // never needs a session. Everything else below runs identically
    // (services mapping, pre-flight, slot window); only the create call and
    // the payment step differ. GUEST-BOOKING-PLAN.md §3.
    const isGuestBooking = !!bookingData?.guest;

    // Check if user is logged in
    const accessToken = localStorage.getItem('accessToken');
    // RESUME PAYMENT FIRST — before the auth gate. Someone who backed out of
    // Stripe already HAS a booking; they just need to pay for it. The auth
    // gate below returns early for anyone without a localStorage session, so a
    // GUEST returning from Stripe was being asked to sign in / re-enter their
    // details and re-verify an OTP instead of going back to the payment screen
    // (Charlie, 2026-08-20).
    if (paymentRetryBookingIdRef.current) {
      const retryBookingId = paymentRetryBookingIdRef.current;
      // A guest has no localStorage session — their booking-scoped token is in
      // sessionStorage. Without it this call goes out unauthenticated and the
      // client never gets back to Stripe.
      let _retryToken = localStorage.getItem('accessToken');
      if (!_retryToken) {
        try { _retryToken = sessionStorage.getItem(`guestManageToken:${retryBookingId}`) || localStorage.getItem(`guestManageToken:${retryBookingId}`); } catch (e) { /* storage unavailable */ }
      }
      setBookingLoaderVisible(true);
      setBookingChargeNow(true);
      setBookingLoaderState(LOADER_STATES.PAYMENT);
      setIsConfirmBookingOpen(false);
      try {
        const paymentResponse = await paymentService.bookingPayment(
          _retryToken ? { bookingId: retryBookingId, accessToken: _retryToken } : { bookingId: retryBookingId }
        );
        const sessionId = paymentResponse.data?.responseData?.result?.id;
        const transactionId = paymentResponse.data?.responseData?.result?.transactionId;
        if (sessionId) {
          localStorage.setItem('bookingId', retryBookingId);
          if (transactionId) localStorage.setItem('transactionId', transactionId);
          setBookingLoaderState(LOADER_STATES.REDIRECTING);
          const stripe = await loadStripe(siteSetting.stripeKey);
          const result = await stripe.redirectToCheckout({ sessionId });
          if (result.error) {
            setBookingLoaderVisible(false);
            bookingSubmittingRef.current = false;
            alert(result.error.message || 'Payment redirect failed');
          }
        } else {
          // Nothing left to pay — the booking stands. Send them to success.
          paymentRetryBookingIdRef.current = null;
          setBookingLoaderState(LOADER_STATES.SUCCESS);
          await new Promise(resolve => setTimeout(resolve, 500));
          window.location.assign(buildBookingSuccessUrl(data, bookingDetails?.stylist?.id, retryBookingId));
        }
      } catch (retryError) {
        console.error('[booking] Payment retry failed:', retryError);
        setBookingLoaderVisible(false);
        bookingSubmittingRef.current = false;
        alert('Could not restart payment. Please try again.');
      }
      return;
    }

    if (!accessToken && !isGuestBooking) {
      // Store pending booking data and show auth prompt modal
      const pendingData = {
        ...bookingData,
        salonId: isProfessionalView ? data?.salonId : (data?.id || salonIdOrSlug),
        hairdresserId: isProfessionalView ? hairdresserId : bookingDetails?.stylist?.id,
        selectedServices: selectedServices,
        bookingDetails: bookingDetails
      };
      setPendingBookingData(pendingData);
      setConfirmStep('auth');
      // Release the double-submit guard: nothing was submitted. Without this
      // the Confirm button is dead for the rest of the session — closing the
      // auth prompt and pressing Confirm again did nothing.
      bookingSubmittingRef.current = false;
      return;
    }
    
    // Returning from a cancelled Stripe checkout: the booking already exists,
    // so skip creation entirely and restart payment for it.

    // Show loader immediately
    setBookingLoaderVisible(true);
    setBookingLoaderState(LOADER_STATES.PREPARING);

    // Close the confirm modal
    setIsConfirmBookingOpen(false);

    try {
      // Get the selected stylist - either from bookingData.stylists or bookingDetails.stylist
      const selectedStylistId = bookingDetails?.stylist?.id ||
        (bookingData.stylists && Object.values(bookingData.stylists).find(s => s)?.id);
      
      if (!selectedStylistId) {
        setBookingLoaderVisible(false);
        bookingSubmittingRef.current = false;
        alert('Please select a stylist');
        return;
      }
      
      // Convert time string (HH:MM) to minutes from midnight
      const timeToMinutes = (timeStr) => {
        if (!timeStr) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };
      
      // The slot picker shows SERVICE start times (after setup) — that's
      // what the customer sees and clicks. To save a bookingSlot that the
      // diary, availability checks, and back-to-back booking logic can all
      // agree on, we anchor bookingSlot[0] at SETUP start (= service start
      // minus the first service's setupTime). The full bookingSlot then
      // represents the visible/reserved window: [setup_start, cleanup_end].
      const serviceStartMinutes = timeToMinutes(bookingDetails?.time);
      // Per-stylist buffers live on s.hairdressers[stylistId]; top-level
      // setupTime etc. is a salon-rollup that's often 0. Pull from the
      // stylist's own copy so the window accounts for their real buffers.
      const pickPerStylist = (s, key, fallbackKey) => {
        if (Array.isArray(s.hairdressers)) {
          const hd = s.hairdressers.find(h => h._id === selectedStylistId);
          const v = hd?.[key];
          if (v !== undefined && v !== null) return parseInt(v, 10) || 0;
        }
        return parseInt(s[fallbackKey || key], 10) || 0;
      };
      // NB: must NOT be named `window` — that shadows the global browser
      // `window` for the rest of this callback, so the post-booking
      // `window.location.assign(...)` success redirects below resolve to this
      // slot object instead (`.location` undefined → "undefined is not an
      // object (evaluating 'window.location.assign')"). Bookings that need no
      // deposit/card-capture hit those redirects directly and crashed.
      const bookingWindow = computeBookingWindow(selectedServices.map(s => ({
        serviceTime: pickPerStylist(s, 'serviceTime', 'duration'),
        setupTime: pickPerStylist(s, 'setupTime'),
        cleanupTime: pickPerStylist(s, 'cleanupTime'),
        processingTime: pickPerStylist(s, 'processingTime'),
        finishingTime: pickPerStylist(s, 'finishingTime'),
      })));
      const totalDuration = bookingWindow.totalSlotMinutes;
      // Anchor at setup_start so the reserved window includes the prep
      // window before the customer-facing service start. Without this,
      // back-to-back bookings would clash on the stylist's actual setup
      // minutes (the diary thinks they're free during prep).
      const startTimeMinutes = serviceStartMinutes - bookingWindow.setupMinutes;
      
      // Format date as YYYY-MM-DD
      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Pre-flight: every grouped service must have a record for the chosen
      // stylist. When the lookup misses (seen when Any-Stylist availability
      // assigns a team member who doesn't offer the service), the old code
      // silently sent the salon-level grouped id — another stylist's record —
      // and the BE rejected it with a raw id in the message, stranding the
      // user behind the loader. Fail fast and recoverably instead.
      const unmappable = selectedServices.filter(s =>
        Array.isArray(s.hairdressers) && s.hairdressers.length > 0 &&
        !s.hairdressers.some(h => h._id === selectedStylistId)
      );
      if (unmappable.length > 0) {
        console.error('[booking] Stylist has no record for selected service(s)', {
          selectedStylistId,
          stylistName: bookingDetails?.stylist?.name,
          services: unmappable.map(s => ({
            id: s.id,
            name: s.name,
            hairdresserIds: (s.hairdressers || []).map(h => h._id)
          }))
        });
        setBookingLoaderVisible(false);
        bookingSubmittingRef.current = false;
        alert(`${bookingDetails?.stylist?.name || 'This stylist'} can't take "${unmappable[0].name}". Please pick a different time or stylist.`);
        // Send them back to the slot picker to re-choose.
        setIsConfirmBookingOpen(false);
        setIsTimesModalOpen(true);
        return;
      }
      
      // Calculate total deposit from services (using hairdresser-specific deposit if available)
      const totalDeposit = selectedServices.reduce((sum, service) => {
        // Look up hairdresser-specific deposit price if available
        let depositPrice = service.depositPrice || 0;
        if (service.hairdressers && Array.isArray(service.hairdressers)) {
          const hairdresserData = service.hairdressers.find(h => h._id === selectedStylistId);
          if (hairdresserData && hairdresserData.depositPrice !== undefined) {
            depositPrice = hairdresserData.depositPrice;
          }
        }
        return sum + (parseFloat(depositPrice) || 0);
      }, 0);
      
      // Discount code: checked again now that we know who's booking (one
      // use each is matched on their phone, or their account). If it can't
      // be used after all, back to the sheet with the reason, never a
      // booking at a price they weren't shown.
      const codeSalonId = isProfessionalView ? data?.salonId : data?.id;
      const codeClientKey = clientKeyOf({ phone: bookingData?.guest?.phoneNumber, userId: isGuestBooking ? null : localStorage.getItem('userId') });
      let promo = DISCOUNTS_VISIBLE && bookingData?.promo && bookingData.promo.code ? bookingData.promo : null;
      if (promo) {
        const bc = bookingCodeRef.current;
        const codeLines = bc.linesFor(selectedServices, selectedStylistId, (s) => getStylistPrice(s, selectedStylistId));
        const codeCtx = { lines: codeLines, date: bookingDetails?.date, client: { phone: bookingData?.guest?.phoneNumber, userId: isGuestBooking ? null : localStorage.getItem('userId') } };
        const again = await checkCode({
          salonId: codeSalonId,
          typed: promo.code,
          lines: codeLines,
          date: formatDate(bookingDetails?.date),
          channel: 'online',
          client: { key: codeClientKey },
          selfEmployedIds: codeLines.map((l) => l.stylistId).filter((id) => isSelfEmployedStylist(id)),
        }).catch(() => ({ ok: false, message: 'Codes can’t be checked just now. Try again in a moment.' }));
        if (!again.ok) {
          setBookingLoaderVisible(false);
          bookingSubmittingRef.current = false;
          if (isGuestBooking) setGuestSubmitting(false);
          bc.check(codeCtx);
          setToast({ isVisible: true, message: again.message || 'That code can’t be used on this booking.', type: 'info' });
          openConfirmAt('summary');
          return;
        }
        promo = {
          ...promo,
          // Only a server that checks codes is sent one (the booking calls
          // refuse fields they don't know); on the device stand-in the saving
          // is still taken off each price, as the till does
          serverChecked: again.source === 'server',
          amount: again.amount,
          lines: selectedServices.map((s, i) => {
            const l = (again.lines || []).find((x) => x.id === `s${i}`);
            return { index: i, serviceId: String(s.commonServiceId || s.id), listPrice: Number(getStylistPrice(s, selectedStylistId)) || 0, discount: l ? Number(l.amount) || 0 : 0 };
          }),
        };
      }

      // Prepare services for booking
      // For "any stylist" flow, we need to use the hairdresser-specific service ID
      // Each hairdresser has their own service record with a unique serviceId
      const servicesToBook = selectedServices.map((service, i) => {
        // Default to the salon-level service ID
        let serviceId = service.id;
        let depositPrice = service.depositPrice || 0;
        // Per-stylist values for buffers + service time. The salon profile API
        // groups services across stylists; top-level service.setupTime etc.
        // is a representative value (often 0) and the real per-stylist values
        // live on service.hairdressers[stylistId]. Read from there when a
        // stylist is selected so the booking persists the right buffers.
        let hairdresserData = null;

        // Look up the hairdresser-specific service ID and deposit price
        if (service.hairdressers && Array.isArray(service.hairdressers)) {
          hairdresserData = service.hairdressers.find(h => h._id === selectedStylistId);
          if (hairdresserData) {
            // Use the hairdresser's individual service ID if available
            if (hairdresserData.serviceId) {
              serviceId = hairdresserData.serviceId;
              console.log(`Mapped service "${service.name}" to hairdresser-specific serviceId: ${serviceId}`);
            }
            // Use hairdresser-specific deposit price if available
            if (hairdresserData.depositPrice !== undefined) {
              depositPrice = hairdresserData.depositPrice;
            }
          }
        }

        const isFirst = i === 0;
        const isLast = i === selectedServices.length - 1;

        // Prefer per-stylist values; fall back to the salon-level (often 0).
        const pickStylistFirst = (key) => {
          const v = hairdresserData?.[key];
          if (v !== undefined && v !== null) return parseInt(v, 10) || 0;
          return parseInt(service[key], 10) || 0;
        };

        const serviceTimeForBooking = hairdresserData?.serviceTime
          ? parseInt(hairdresserData.serviceTime, 10) || 0
          : (parseInt(service.duration, 10) || 0);

        // A code's saving on this service is taken off its price, as the
        // till does; promo.lines carries the menu price for the server.
        const listPrice = getStylistPrice(service, selectedStylistId);
        const promoLine = promo && promo.lines ? promo.lines.find((l) => l.index === i) : null;
        const lineOff = promoLine ? Math.min(Number(promoLine.discount) || 0, Number(listPrice) || 0) : 0;

        return {
          _id: serviceId,
          serviceId: serviceId,
          serviceName: service.name,
          servicePrice: lineOff > 0 ? Math.round((Number(listPrice) - lineOff) * 100) / 100 : listPrice,
          serviceTime: serviceTimeForBooking,
          // Never a deposit above what the client now pays for it
          depositPrice: lineOff > 0 ? Math.min(Number(depositPrice) || 0, Math.round((Number(listPrice) - lineOff) * 100) / 100) : depositPrice,
          processingTime: pickStylistFirst('processingTime'),
          finishingTime: pickStylistFirst('finishingTime'),
          // Multi-service rule: zero out non-edge buffers on the wire.
          setupTime: isFirst ? pickStylistFirst('setupTime') : 0,
          cleanupTime: isLast ? pickStylistFirst('cleanupTime') : 0,
        };
      });
      
      // With a code on, the deposit is never more than the discounted price
      // (each service's deposit was capped above)
      const depositWithCode = promo ? servicesToBook.reduce((sum, x) => sum + (Number(x.depositPrice) || 0), 0) : totalDeposit;

      // Check if running in mobile app
      const isNativeApp = Capacitor.isNativePlatform();
      
      // Create booking params for v2 online booking API
      // This API properly adds the client to the hairdresser's client list
      // based on data sharing settings
      // Multi-location: the site this page is booking at. A stylist (and every
      // owner) can hold a record at several locations; without it the server
      // can't tell which one the booking is for. Resolved id only, never a slug.
      const bookingSalonRef = isProfessionalView ? data?.salonId : data?.id;
      const bookingSalonId = /^[0-9a-fA-F]{24}$/.test(String(bookingSalonRef || '')) ? String(bookingSalonRef) : undefined;

      const params = {
        servicesToBook,
        hairdresserId: selectedStylistId,
        userId: selectedStylistId, // Keep for backwards compatibility
        salonId: bookingSalonId,
        bookingSlot: [startTimeMinutes, startTimeMinutes + totalDuration],
        bookingDate: formatDate(bookingDetails?.date),
        deviceToken: 'web',
        isPayAtSalonBooking: 1,
        clientId: '',
        accessToken,
        isOnlineBooking: 1,
        fromMobile: isNativeApp ? 1 : 0,
        // Acquisition channel drives client ownership (client-flows §4a):
        // a booking made on the SALON's profile is salon-acquired; one made on
        // a stylist's OWN profile/booking link is stylist-acquired.
        acquisitionChannel: isProfessionalView ? 'pro_link' : 'salon_profile',
        // Gift card redeemed at confirm time rides along as { id, code,
        // amount }. The BE validates it, redeems the balance, writes a
        // paymentType-11 payment leg on the booking (salon-visible) and
        // charges Stripe only the deposit remainder.
        giftCard: bookingData?.giftCard || null,
        // Per-client marketing consent for the Rule 1 owner (§5A). Sent on
        // every create; the BE re-resolves the owner and records against
        // that. `undefined` when the client wasn't asked (repeat booking).
        marketingConsent: bookingData?.marketingConsent || undefined,
        // The tracked link (?src=) they came in on, if it's fresh: reporting only
        ...trackedSourceFor(bookingSalonId),
        // The discount code, checked just above: the server applies and holds
        // it (docs/DISCOUNT_CODES_BACKEND.md). Prices above are already after it.
        ...(promo && promo.serverChecked ? { promo: promoPayload(promo) } : {}),
      };

      console.log('Creating online booking with v2 API params:', params);

      // Update loader state to creating
      setBookingLoaderState(LOADER_STATES.CREATING);

      // Create the booking. Guests hit the no-auth endpoint (pay-at-salon
      // only, unclaimed client record); everyone else the v2 online-booking
      // API which adds the client to the hairdresser's client list.
      const response = isGuestBooking
        ? await bookingService.createGuestBooking({
            hairdresserId: selectedStylistId,
            servicesToBook,
            bookingDate: params.bookingDate,
            bookingSlot: params.bookingSlot,
            salonId: bookingSalonId,
            guest: bookingData.guest,
            marketingConsent: bookingData?.marketingConsent || undefined,
            ...trackedSourceFor(bookingSalonId),
            ...(promo && promo.serverChecked ? { promo: promoPayload(promo) } : {}),
          })
        : await bookingService.createOnlineBookingV2(params);

      if (response.data?.errorCode || response.data?.statusCode === 0) {
        setBookingLoaderVisible(false);
        bookingSubmittingRef.current = false;
        // Guest: the email belongs to a CLAIMED account (§4.2 step 3). Don't
        // create anything; steer them to sign in with the booking preserved.
        if (isGuestBooking && response.data?.outcome === 'account_exists') {
          setGuestSubmitting(false);
          setGuestSubmitError('Looks like you already have a Beu account with that email. Please sign in to continue.');
          openConfirmAt('guest-details');
          return;
        }
        const failMsg = response.data?.error?.responseMessage || response.data?.messages || '';
        // The slot was taken while they typed. Leaving them on the details
        // form with an error is a dead end — the fix is a different time, so
        // send them back to the slot picker. TimesModal remounts on open and
        // refetches availability; selectedStylist is untouched so their
        // stylist stays chosen. Their details survive in pendingGuest /
        // pendingBookingData and are pre-filled on the next attempt.
        if (/slot.*(no longer|not) available|no longer available|already (been )?booked|time.*(taken|unavailable)/i.test(failMsg)) {
          setGuestSubmitting(false);
          setIsConfirmBookingOpen(false);
          setIsTimesModalOpen(true);
          setToast({ isVisible: true, message: 'That time was just taken. Pick another slot.', type: 'info' });
          return;
        }
        if (isGuestBooking) {
          setGuestSubmitting(false);
          setGuestSubmitError(failMsg || 'We could not make this booking. Please try again.');
          openConfirmAt('guest-details');
          return;
        }
        alert(failMsg || 'Failed to create booking');
        return;
      }

      // Handle all possible response shapes:
      // v1: response.data.data.bookingInfo
      // v2: response.data.responseData.data.bookingInfo
      // v2 alt: response.data.responseData.bookingInfo (if backend omits the extra .data wrapper)
      // guest: response.data.responseData.bookingId (+ manageToken)
      const rd = response.data?.responseData || response.data;

      // Guest create WITHOUT a prior code (salon's OTP switch off) hitting a
      // CLAIMED email: nothing was created — fall back to the OTP sign-in so
      // the booking lands on their real account. The send masks the contact
      // (sentTo), same as the send-otp path.
      if (isGuestBooking && (rd?.outcome === 'account_exists' || rd?.data?.outcome === 'account_exists')) {
        setBookingLoaderVisible(false);
        bookingSubmittingRef.current = false;
        setGuestSubmitting(false);
        try {
          const otpResp = await bookingService.sendGuestOtp({
            email: bookingData.guest.email,
            phoneNumber: bookingData.guest.phoneNumber,
          });
          const ord = otpResp.data?.responseData || otpResp.data;
          setGuestOtpVariant('account_exists');
          setGuestOtpSentTo(ord?.sentTo || '');
          setGuestOtpError('');
          openConfirmAt('otp');
        } catch (otpErr) {
          setGuestSubmitError('Looks like you already have a Beu account with that email. Please sign in to continue.');
          openConfirmAt('guest-details');
        }
        return;
      }

      const bookingInfo = rd?.data?.bookingInfo || rd?.bookingInfo || response.data?.data?.bookingInfo;
      const bookingId = bookingInfo?.id || bookingInfo?._id || rd?.data?.bookingId || rd?.bookingId;

      // The code's use is held on this booking (the device stand-in needs
      // telling; the server does it from `promo`). Never blocks the booking.
      if (promo && bookingId) {
        holdCode({
          salonId: codeSalonId,
          code: promo.code,
          bookingId,
          clientKey: codeClientKey,
          amount: promo.amount,
          total: servicesToBook.reduce((sum, x) => sum + (Number(x.servicePrice) || 0), 0),
          channel: 'online',
        }).catch(() => {});
      }

      if (!bookingId) {
        console.error('[booking] Could not extract booking ID from response:', JSON.stringify(response.data));
        setBookingLoaderVisible(false);
        bookingSubmittingRef.current = false;
        alert('Booking created but no booking ID returned');
        return;
      }

      // A GUEST IS NEVER LOGGED IN. The BE returns a real token for the
      // unclaimed record, but writing it to localStorage would flip the whole
      // app into "logged-in / you have an account" chrome — which contradicts
      // the no-account promise (a guest who abandons at payment must not come
      // back to find themselves signed in). So we stash the token in
      // sessionStorage, scoped to this booking: enough for the success page to
      // show the booking and for the deposit step to authenticate, but the
      // app never treats them as an account holder. Becoming a real account is
      // the optional claim ("set a password") on the success page.
      // NB the existing-account path (verify → 'logged_in') DOES persist a
      // real localStorage session — that genuinely is a sign-in.
      let guestAccessToken = null;
      if (isGuestBooking) {
        const guestData = rd?.data || rd;
        guestAccessToken = guestData?.accessToken || null;
        try {
          if (guestAccessToken) sessionStorage.setItem(`guestManageToken:${bookingId}`, guestAccessToken);
          // A copy in localStorage for the success page: in-app browsers
          // (Instagram) drop sessionStorage across the Stripe redirect. Its own
          // key, never 'accessToken', so the guest is still not signed in.
          if (guestAccessToken) localStorage.setItem(`guestManageToken:${bookingId}`, guestAccessToken);
          // Stash the guest's email too, so the success page's "set a password"
          // claim works without the booking-detail response having to expose it.
          const guestEmail = bookingData?.guest?.email;
          if (guestEmail) sessionStorage.setItem(`guestEmail:${bookingId}`, guestEmail);
        } catch (e) { /* storage unavailable */ }
        setGuestSubmitting(false);

        // Nothing due online → straight to the guest success page. Anything
        // needing a card (deposit or card-capture policy) falls through to the
        // shared Stripe continuation below — the guest token is threaded to
        // bookingPayment() explicitly, so no login is needed for it either.
        const guestNeedsPayment = !!(bookingInfo?.isDepositPay || bookingInfo?.isCardCapture);
        if (!guestNeedsPayment) {
          setSelectedServices([]);
          setBookingDetails({});
          setBookingLoaderState(LOADER_STATES.SUCCESS);
          await new Promise(resolve => setTimeout(resolve, 800));
          window.location.assign(buildBookingSuccessUrl(data, selectedStylistId, bookingId) + '&guest=1');
          return;
        }
        // else: continue into the deposit/card-capture path shared with the
        // logged-in flow (guestAccessToken is passed to bookingPayment below).
      }

      // Get deposit and policy info
      const isDepositPay = bookingInfo?.isDepositPay || false;
      const depositAmount = bookingInfo?.depositAmount || depositWithCode;
      
      // Check cancellation policy and no-show status from the hairdresser/staff data
      // For professional view, use data directly; for salon view, look up from services hairdresser data
      let noShowStatus = 0;
      let hasCancellationPolicy = false;
      
      if (isProfessionalView && data) {
        // Professional view - use policy data from staff summary
        noShowStatus = data.noShowStatus || 0;
        hasCancellationPolicy = data.cancellationPolicy &&
                               Array.isArray(data.cancellationPolicy) &&
                               data.cancellationPolicy.length > 0;
      } else if (!isProfessionalView && selectedServices.length > 0) {
        // Salon view - look up the selected hairdresser's policy data from the services
        // The services contain hairdresser-specific policy data
        const firstService = selectedServices[0];
        if (firstService.hairdressers && Array.isArray(firstService.hairdressers)) {
          const hairdresserData = firstService.hairdressers.find(h => h._id === selectedStylistId);
          if (hairdresserData) {
            // Check if hairdresser is salon owner or self-employed (employmentTypeId === 2)
            // In those cases, use their own policy; otherwise use salon owner's policy
            if (hairdresserData.isSalonOwner || hairdresserData.employmentTypeId === 2) {
              noShowStatus = hairdresserData.noShowStatus || 0;
              hasCancellationPolicy = hairdresserData.cancellationPolicy &&
                                     Array.isArray(hairdresserData.cancellationPolicy) &&
                                     hairdresserData.cancellationPolicy.length > 0;
            } else {
              // Employee - look for salon owner's policy in the hairdressers array
              const salonOwnerData = firstService.hairdressers.find(h => h.isSalonOwner);
              if (salonOwnerData) {
                noShowStatus = salonOwnerData.noShowStatus || 0;
                hasCancellationPolicy = salonOwnerData.cancellationPolicy &&
                                       Array.isArray(salonOwnerData.cancellationPolicy) &&
                                       salonOwnerData.cancellationPolicy.length > 0;
              }
            }
            
            console.log('Salon view - policy check from services:', {
              selectedHairdresserId: selectedStylistId,
              hairdresserData: hairdresserData,
              isSalonOwner: hairdresserData.isSalonOwner,
              employmentTypeId: hairdresserData.employmentTypeId,
              noShowStatus,
              hasCancellationPolicy
            });
          }
        }
      }
      
      // Check if we need to redirect to Stripe for payment/card capture.
      // An applied gift card pre-pays the deposit (the BE wrote it as a
      // paymentType-11 leg on the booking and charges only the remainder),
      // so the Stripe step is skipped when the card covers the whole deposit
      // and no card-capture policy applies.
      const giftCardApplied = parseFloat(bookingData?.giftCard?.amount) || 0;
      const depositDueNow = Math.max(0, depositWithCode - giftCardApplied);
      // TRUST THE BOOKING the BE just returned, not only our local recompute.
      // The recompute reads selectedServices, and after a Stripe back-out those
      // are REBUILT from the API — if any deposit/policy detail is missing there
      // it wrongly concluded nothing was due and sent the client to the success
      // page WITHOUT taking the deposit or card (Charlie, 2026-08-20). The BE is
      // authoritative: isCardCapture/isDepositPay on the created booking say
      // definitively whether Stripe is required.
      const bookingSaysPaymentDue = !!(bookingInfo?.isCardCapture || bookingInfo?.isDepositPay);
      const needsPayment = bookingSaysPaymentDue || depositDueNow > 0 || noShowStatus === 1 || hasCancellationPolicy;

      console.log('Payment decision:', {
        totalDeposit,
        giftCardApplied,
        depositDueNow,
        depositAmount,
        isDepositPay,
        noShowStatus,
        hasCancellationPolicy,
        needsPayment,
        bookingId
      });

      // Reset state
      setSelectedServices([]);
      setBookingDetails({});

      if (needsPayment) {
        // Update loader to payment state. Only a deposit means money is taken;
        // a no-show/cancellation policy with no deposit is card-capture only.
        setBookingChargeNow(depositDueNow > 0);
        setBookingLoaderState(LOADER_STATES.PAYMENT);
        
        // Need to redirect to Stripe for deposit payment or card capture.
        // Guests aren't logged in (no localStorage token), so pass their
        // booking-scoped token explicitly.
        try {
          const paymentResponse = await paymentService.bookingPayment(
            guestAccessToken ? { bookingId, accessToken: guestAccessToken } : { bookingId }
          );
          
          if (paymentResponse.data?.errorCode) {
            setBookingLoaderVisible(false);
            alert(paymentResponse.data.messages || 'Failed to initiate payment');
            // Still redirect to success page even if payment setup fails
            window.location.assign(buildBookingSuccessUrl(data, selectedStylistId, bookingId));
            return;
          }
          
          const sessionId = paymentResponse.data?.responseData?.result?.id;
          const transactionId = paymentResponse.data?.responseData?.result?.transactionId;
          
          if (sessionId) {
            // Store booking info for after payment
            localStorage.setItem('bookingId', bookingId);
            if (transactionId) {
              localStorage.setItem('transactionId', transactionId);
            }
            
            // Update loader to redirecting state
            setBookingLoaderState(LOADER_STATES.REDIRECTING);
            
            // Small delay to show the redirecting message
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Load Stripe and redirect to checkout
            const stripe = await loadStripe(siteSetting.stripeKey);
            if (stripe) {
              const result = await stripe.redirectToCheckout({ sessionId });
              if (result.error) {
                console.error('Stripe redirect error:', result.error);
                setBookingLoaderVisible(false);
                alert(result.error.message || 'Payment redirect failed');
              }
            } else {
              setBookingLoaderVisible(false);
              alert('Failed to load payment system');
            }
          } else {
            // No session ID, redirect to success page
            setBookingLoaderState(LOADER_STATES.SUCCESS);
            await new Promise(resolve => setTimeout(resolve, 500));
            window.location.assign(buildBookingSuccessUrl(data, selectedStylistId, bookingId));
          }
        } catch (paymentError) {
          console.error('Payment error:', paymentError);
          setBookingLoaderVisible(false);
          // Redirect to success page even if payment fails
          window.location.assign(buildBookingSuccessUrl(data, selectedStylistId, bookingId));
        }
      } else {
        // No payment needed, show success and redirect
        setBookingLoaderState(LOADER_STATES.SUCCESS);
        await new Promise(resolve => setTimeout(resolve, 800));
        window.location.assign(buildBookingSuccessUrl(data, selectedStylistId, bookingId));
      }
      
    } catch (error) {
      console.error('Error creating booking:', error);
      setBookingLoaderVisible(false);
      bookingSubmittingRef.current = false;
      if (isGuestBooking) {
        // Put the guest sheet back with the error rather than a bare alert.
        setGuestSubmitting(false);
        setGuestSubmitError('We could not make this booking. Please check your details and try again.');
        openConfirmAt('guest-details');
        return;
      }
      alert(error.message || 'Failed to create booking. Please try again.');
    }
  }, [bookingDetails, selectedServices, history, isProfessionalView, data, salonIdOrSlug, hairdresserId, buildBookingSuccessUrl, openConfirmAt, isSelfEmployedStylist]);

  // Guest sheet submit: instead of booking straight away, request an email
  // verification code and open the OTP step. The BE won't create a booking
  // (or reveal an existing account) without a verified code, so nothing is
  // written until the client proves they own the email.
  const handleGuestSubmit = useCallback(async (guest) => {
    if (!pendingBookingData) {
      setGuestSubmitError('Your booking details were lost. Please choose a time again.');
      return;
    }
    // Guests are never asked for marketing consent and can't be marketed to
    // until they claim an account, so nothing is sent — the BE also forces it
    // off for the guest path regardless.
    setPendingGuest({ details: guest, marketingConsent: undefined });

    // Verification code switched off for this booking's stylist/salon:
    // book straight away, no code step. If the email turns out to belong to
    // a claimed account, the create returns account_exists and the handler
    // below falls back to the OTP sign-in — identity is still proven where
    // it matters.
    if (!otpRequiredForBooking) {
      setGuestSubmitError('');
      handleConfirmBooking({
        ...pendingBookingData,
        guest,
        marketingConsent: undefined,
      });
      return;
    }

    setGuestSubmitting(true);
    setGuestSubmitError('');
    try {
      const resp = await bookingService.sendGuestOtp({
        email: guest.email,
        phoneNumber: guest.phoneNumber,
      });
      const rd = resp.data?.responseData || resp.data;
      if (resp.data?.statusCode === 0 || resp.data?.errorCode) {
        setGuestSubmitError(rd?.message || resp.data?.error?.responseMessage || 'We could not send a code. Please try again.');
        return;
      }
      const isAccountExists = rd?.outcome === 'account_exists';
      setGuestOtpVariant(isAccountExists ? 'account_exists' : 'guest');
      // A guest typed their own address — show it in full. Only mask on the
      // account-exists path, where the code goes to the account's registered
      // contact (which we must not reveal to whoever's booking).
      setGuestOtpSentTo(isAccountExists ? (rd?.sentTo || '') : guest.email);
      setGuestOtpError('');
      setConfirmStep('otp');
    } catch (err) {
      setGuestSubmitError(err?.response?.data?.error?.responseMessage || 'We could not send a code. Please try again.');
    } finally {
      setGuestSubmitting(false);
    }
  }, [pendingBookingData, consentContext, otpRequiredForBooking, handleConfirmBooking]);

  // OTP verified: two outcomes.
  //  - 'verified'  → a genuine guest; proceed to the guest booking create
  //    (the BE now has a verified code to consume).
  //  - 'logged_in' → the email was a claimed account; the response carries a
  //    real session. Persist it and run the booking through the NORMAL
  //    logged-in path (no `guest` key) so it lands on their real account.
  const handleGuestOtpVerify = useCallback(async (code) => {
    if (!pendingGuest) {
      setGuestOtpError('Your booking details were lost. Please choose a time again.');
      return;
    }
    setGuestOtpSubmitting(true);
    setGuestOtpError('');
    try {
      const resp = await bookingService.verifyGuestOtp({
        email: pendingGuest.details.email,
        code,
      });
      const rd = resp.data?.responseData || resp.data;
      if (resp.data?.statusCode === 0 || resp.data?.errorCode) {
        setGuestOtpError(rd?.message || resp.data?.error?.responseMessage || 'That code was not valid. Please try again.');
        setGuestOtpSubmitting(false);
        return;
      }

      if (rd?.outcome === 'logged_in') {
        // Passwordless sign-in — same session persistence as MagicLinkLanding.
        try {
          if (rd.accessToken) localStorage.setItem('accessToken', rd.accessToken);
          if (rd.refreshToken) localStorage.setItem('refreshToken', rd.refreshToken);
          if (rd.userId) localStorage.setItem('userId', String(rd.userId));
        } catch (e) { /* storage unavailable */ }
        setGuestOtpSubmitting(false);
        // Book as the now-authenticated client (drop the guest payload).
        handleConfirmBooking({
          ...pendingBookingData,
          marketingConsent: pendingGuest.marketingConsent,
        });
        return;
      }

      // 'verified' → guest booking create.
      setGuestOtpSubmitting(false);
      handleConfirmBooking({
        ...pendingBookingData,
        guest: pendingGuest.details,
        marketingConsent: pendingGuest.marketingConsent,
      });
    } catch (err) {
      setGuestOtpError(err?.response?.data?.error?.responseMessage || 'That code was not valid. Please try again.');
      setGuestOtpSubmitting(false);
    }
  }, [pendingGuest, pendingBookingData, handleConfirmBooking]);

  const handleGuestOtpResend = useCallback(async () => {
    if (!pendingGuest) return;
    setGuestOtpError('');
    try {
      const resp = await bookingService.sendGuestOtp({
        email: pendingGuest.details.email,
        phoneNumber: pendingGuest.details.phoneNumber,
      });
      const rd = resp.data?.responseData || resp.data;
      // Keep the same masking rule as the first send (unmasked for guests).
      if (rd?.outcome === 'account_exists') {
        if (rd?.sentTo) setGuestOtpSentTo(rd.sentTo);
      } else {
        setGuestOtpSentTo(pendingGuest.details.email);
      }
    } catch (err) {
      setGuestOtpError('We could not resend the code. Please try again shortly.');
    }
  }, [pendingGuest]);

  // Handle share - copy URL to clipboard
  const handleShare = useCallback(async () => {
    const currentUrl = window.location.href;
    const shareText = isProfessionalView
      ? `Check out ${data?.name || 'this stylist'} on Beu!`
      : `Check out ${data?.name || 'this salon'} on Beu!`;
    
    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: data?.name || 'Beu',
          text: shareText,
          url: currentUrl,
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        if (err.name !== 'AbortError') {
          console.log('Share failed, falling back to clipboard');
        }
      }
    }
    
    // Fall back to clipboard copy
    try {
      await navigator.clipboard.writeText(currentUrl);
      setToast({
        isVisible: true,
        message: 'Link copied to clipboard!',
        type: 'success'
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setToast({
          isVisible: true,
          message: 'Link copied to clipboard!',
          type: 'success'
        });
      } catch (copyErr) {
        setToast({
          isVisible: true,
          message: 'Failed to copy link',
          type: 'error'
        });
      }
      document.body.removeChild(textArea);
    }
  }, [data, isProfessionalView]);
  
  // Handle favorite toggle
  const handleFavorite = useCallback(async () => {
    const isLoggedIn = localStorage.getItem('accessToken');
    
    if (!isLoggedIn) {
      // Show auth prompt modal for unauthenticated users
      setAuthPromptContext('favorite');
      setIsAuthPromptOpen(true);
      return;
    }
    
    // Trigger heart animation
    setIsAnimatingHeart(true);
    
    // Toggle favorite state
    const newFavoriteState = !isFavorited;
    setIsFavorited(newFavoriteState);
    
    // Show toast notification
    const entityType = isProfessionalView ? 'stylist' : 'salon';
    setToast({
      isVisible: true,
      message: newFavoriteState
        ? `Added ${data?.name || entityType} to favorites!`
        : `Removed ${data?.name || entityType} from favorites`,
      type: newFavoriteState ? 'heart' : 'success'
    });
    
    // Reset animation after it completes
    setTimeout(() => {
      setIsAnimatingHeart(false);
    }, 600);
    
    // Save to backend
    try {
      if (isProfessionalView) {
        await marketplaceService.toggleFavoriteStylist({
          stylistId: hairdresserId,
          isFavorite: newFavoriteState
        });
      } else {
        await marketplaceService.toggleFavoriteSalon({
          salonId: data?.id || salonIdOrSlug,
          isFavorite: newFavoriteState
        });
      }
    } catch (err) {
      console.error('Failed to save favorite:', err);
      // Revert on error
      setIsFavorited(!newFavoriteState);
      setToast({
        isVisible: true,
        message: 'Failed to save favorite. Please try again.',
        type: 'error'
      });
    }
  }, [isFavorited, isProfessionalView, data, history, salonIdOrSlug, hairdresserId]);
  
  // Close toast handler
  const closeToast = useCallback(() => {
    setToast(prev => ({ ...prev, isVisible: false }));
  }, []);
  
  // Open services modal
  const openServicesModal = useCallback(() => {
    if (!bookingOpen) return;
    setIsServicesModalOpen(true);
  }, [bookingOpen]);
  
  // Scroll to section
  const scrollToSection = useCallback((sectionName) => {
    const refs = {
      Services: servicesRef,
      Team: teamRef,
      // TEMPORARILY COMMENTED OUT - Shop section
      // Shop: shopRef,
      'Gift Cards': giftCardsRef,
      Info: infoRef,
      Locations: locationsRef,
    };
    
    const ref = refs[sectionName];
    if (ref?.current) {
      const offset = 80;
      const top = ref.current.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);
  
  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarIcon key={i} filled={true} />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<StarIcon key={i} filled={true} />);
      } else {
        stars.push(<StarIcon key={i} filled={false} />);
      }
    }
    
    return stars;
  };
  
  // Handle back navigation. goBack() is only safe when THIS load arrived by
  // an in-app PUSH. When the button is shown from the remembered session flag
  // (fresh load after a signup/Stripe redirect, or back/forward), the real
  // history stack is polluted — goBack() could land on the signup page or
  // leave the app — so go to the marketplace explicitly: that's where "back"
  // leads for a client who browsed in. On a direct link to a salon-scoped
  // professional page there is no in-app history to pop, so go up to the
  // salon the page belongs to.
  const handleBack = () => {
    if (backNav.pushed) {
      history.goBack();
      return;
    }
    if (canGoBack) {
      history.push('/');
      return;
    }
    if (salonIdOrSlug) history.push(`/book/${salonIdOrSlug}`);
  };
  
  // Hero carousel swipe handlers
  const minSwipeDistance = 50;
  
  /* Open the full-screen viewer on the image currently shown. Professional
     view only: the salon page has no portfolio behind the hero, so there is
     nothing for the viewer to show there. */
  const openHeroViewer = useCallback(() => {
    if (!isProfessionalView) return;
    if (!data?.heroImages?.length) return;
    setPortfolioGalleryInitialIndex(currentImageIndex);
    setIsPortfolioGalleryOpen(true);
  }, [isProfessionalView, data?.heroImages?.length, currentImageIndex]);

  const handleHeroTouchStart = useCallback((e) => {
    const t = e.targetTouches[0];
    heroTap.current = { x: t.clientX, y: t.clientY, moved: false };
    setHeroTouchEnd(null);
    setHeroTouchStart(t.clientX);
  }, []);

  const handleHeroTouchMove = useCallback((e) => {
    const t = e.targetTouches[0];
    if (!heroTap.current.moved
      && Math.abs(t.clientX - heroTap.current.x) + Math.abs(t.clientY - heroTap.current.y) > HERO_TAP_SLOP) {
      heroTap.current.moved = true;
    }
    setHeroTouchEnd(t.clientX);
  }, []);

  const handleHeroTouchEnd = useCallback(() => {
    // A tap that never moved opens the viewer instead of paging the carousel.
    if (!heroTap.current.moved) {
      openHeroViewer();
      return;
    }
    if (!heroTouchStart || !heroTouchEnd) return;

    const distance = heroTouchStart - heroTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Get the current heroImages length from data
    const totalImages = data?.heroImages?.length || 1;
    
    if (isLeftSwipe && currentImageIndex < totalImages - 1) {
      setCurrentImageIndex(prev => prev + 1);
    } else if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
  }, [heroTouchStart, heroTouchEnd, currentImageIndex, data?.heroImages?.length, openHeroViewer]);

  /* Desktop drag-to-swipe. Mouse events only — touch devices already have
     the handlers above, and pointer events would fire for both and advance
     the carousel twice on a single phone swipe. */
  const handleHeroMouseDown = useCallback((e) => {
    // Left button only, and never start a drag from the overlaid controls.
    if (e.button !== 0) return;
    if (e.target.closest('button')) return;
    heroTap.current = { x: e.clientX, y: e.clientY, moved: false };
    setHeroDragStart(e.clientX);
    setHeroDragDelta(0);
  }, []);

  const handleHeroMouseMove = useCallback((e) => {
    if (heroDragStart === null) return;
    if (!heroTap.current.moved
      && Math.abs(e.clientX - heroTap.current.x) + Math.abs(e.clientY - heroTap.current.y) > HERO_TAP_SLOP) {
      heroTap.current.moved = true;
    }
    // Suppresses the browser's native image-drag ghost mid-gesture.
    e.preventDefault();
    setHeroDragDelta(e.clientX - heroDragStart);
  }, [heroDragStart]);

  const handleHeroMouseUp = useCallback(() => {
    if (heroDragStart === null) return;
    const totalImages = data?.heroImages?.length || 1;
    if (heroDragDelta < -minSwipeDistance && currentImageIndex < totalImages - 1) {
      setCurrentImageIndex(prev => prev + 1);
    } else if (heroDragDelta > minSwipeDistance && currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
    // Reset regardless, so a short drag springs back to the current slide.
    setHeroDragStart(null);
    setHeroDragDelta(0);
    // A click that never moved is a tap, not a drag: open the viewer.
    if (!heroTap.current.moved) openHeroViewer();
  }, [heroDragStart, heroDragDelta, currentImageIndex, data?.heroImages?.length, openHeroViewer]);

  /* A drag that ends outside the carousel (or off-window) never fires the
     element's mouseup, which would leave the slide stuck mid-drag. Listening
     on window while a drag is active closes that off-by-one. */
  useEffect(() => {
    if (heroDragStart === null) return undefined;
    window.addEventListener('mouseup', handleHeroMouseUp);
    return () => window.removeEventListener('mouseup', handleHeroMouseUp);
  }, [heroDragStart, handleHeroMouseUp]);

  // Hero carousel arrow navigation handlers
  const goToHeroPrev = useCallback((e) => {
    e.stopPropagation();
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
  }, [currentImageIndex]);

  const goToHeroNext = useCallback((e) => {
    e.stopPropagation();
    const totalImages = data?.heroImages?.length || 1;
    if (currentImageIndex < totalImages - 1) {
      setCurrentImageIndex(prev => prev + 1);
    }
  }, [currentImageIndex, data?.heroImages?.length]);
  
  // Handle book button click
  const handleBook = () => {
    if (!bookingOpen) return;
    if (isProfessionalView) {
      // For professional view, go directly to services modal
      openServicesModal();
    } else {
      // For salon view, show stylist selection or auto-select if only 1 stylist
      openStylistSelectionOrAutoSelect();
    }
  };
  
  // Handle stylist selection
  const handleStylistSelect = (stylist) => {
    setSelectedStylist(stylist);
    setIsStylistSelectionOpen(false);
    // After selecting stylist, open services modal
    setIsServicesModalOpen(true);
  };
  
  // Handle team member selection
  const handleTeamMemberClick = (memberId, memberSlug) => {
    if (!bookingOpen) return;
    const base = data?.slug ? `/book/${data.slug}` : `/salon/${salonIdOrSlug}`;
    const professionalRef = memberSlug || memberId;
    history.push(`${base}/professional/${professionalRef}`);
  };

  // Handle viewing stylist profile from the stylist selection modal (with services pre-selected)
  const handleViewStylistProfile = (memberId) => {
    if (!bookingOpen) return;
    // Close the stylist selection modal
    setIsStylistSelectionOpen(false);

    const base = data?.slug ? `/book/${data.slug}` : `/salon/${salonIdOrSlug}`;

    // Find the member's slug if available
    const member = data?.team?.find(m => m.id === memberId);
    const professionalRef = member?.slug || memberId;

    // Store the selected services in localStorage so the professional page can restore them
    if (selectedServices.length > 0) {
      const profileViewData = {
        selectedServices: selectedServices,
        returnUrl: `${base}/professional/${professionalRef}`,
        openServicesModal: true,
        savedAt: Date.now()
      };
      localStorage.setItem('pendingBooking', JSON.stringify(profileViewData));
    }

    // Navigate to the professional page
    history.push(`${base}/professional/${professionalRef}`);
  };
  
  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };
  
  // Check if image URL is valid
  const isValidImage = (url) => {
    if (!url) return false;
    if (url === 'https://via.placeholder.com/100') return false;
    if (url.includes('placeholder')) return false;
    if (url === '') return false;
    return true;
  };
  
  // Resolve who owns this booking's client under Rule 1 (marketing
  // attribution) so the consent checkbox in the confirm / guest sheets can
  // name the right sender BEFORE submit. Backend-only knowledge (14 days of
  // campaign engagement), so we ask. Failure is non-fatal: the checkbox
  // falls back to naming the salon and the BE records against its own
  // resolution anyway. See GUEST-BOOKING-PLAN.md §5A.
  const consentStylistId = isProfessionalView
    ? hairdresserId
    : (pendingBookingData?.bookingDetails?.stylist?.id || bookingDetails?.stylist?.id);
  const consentSalonRef = isProfessionalView ? data?.salonId : (data?.id || salonIdOrSlug);
  // The guest-details step lives inside the Confirm sheet now, so one flag.
  const consentSheetOpen = isConfirmBookingOpen;
  useEffect(() => {
    if (!consentSheetOpen) return undefined;
    let cancelled = false;
    const stylistId = consentStylistId;
    const salonRef = consentSalonRef;
    if (!stylistId || !salonRef) return undefined;
    bookingService.getBookingConsentContext({ salonId: salonRef, hairdresserId: stylistId })
      .then((res) => {
        if (cancelled) return;
        const rd = res?.data?.responseData || res?.data;
        const ctx = rd?.data || rd;
        if (ctx && ctx.ownerId) {
          setConsentContext({
            ownerId: ctx.ownerId,
            ownerType: ctx.ownerType,
            ownerName: ctx.ownerName,
            alreadyConsented: !!ctx.alreadyConsented,
            showConsent: ctx.showConsent !== false,
          });
        } else {
          setConsentContext(null);
        }
      })
      .catch(() => { if (!cancelled) setConsentContext(null); });
    return () => { cancelled = true; };
  // Primitive deps only: re-fetch when a sheet opens or the stylist/salon
  // changes, not on every unrelated re-render of the objects they came from.
  }, [consentSheetOpen, consentStylistId, consentSalonRef]);

  // Effect to continue booking after returning from login
  useEffect(() => {
    if (shouldContinueBooking && restoredBookingData) {
      // Reset the flag
      setShouldContinueBooking(false);
      
      // Open the confirm booking modal after a short delay to let user review and confirm
      setTimeout(() => {
        setIsConfirmBookingOpen(true);
        setRestoredBookingData(null);
      }, 500);
    }
  }, [shouldContinueBooking, restoredBookingData]);

  // Loading state - Show skeleton only (no popup overlay)
  if (loading) {
    return isProfessionalView ? <ProfessionalProfileSkeleton /> : <SalonProfileSkeleton />;
  }

  if (brandData) {
    return <PublicBrandPage data={brandData} />;
  }
  
  // Error state
  if (error) {
    return (
      <div className="salon-profile-page">
        <div className="error-container">
          <h2>Unable to load salon</h2>
          <p>{error}</p>
          <button onClick={() => history.goBack()} className="back-button-error">
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // No data state
  if (!data) {
    return (
      <div className="salon-profile-page">
        <div className="error-container">
          <h2>Salon not found</h2>
          <button onClick={() => history.goBack()} className="back-button-error">
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // Get current hero image - for professional view, use staffImage from the transformed data
  // For salon view, show all hero images (main image + gallery images) in a swipeable carousel
  /* heroImages is already built correctly per view further up (a pro gets
     their portfolio, falling back to their avatar). This previously forced the
     avatar again for professionals, overriding that and re-creating the
     stretched head-shot banner. Just use what was built. */
  const heroImages = data.heroImages || [];
  const currentImage = heroImages[currentImageIndex] || heroImages[0] || (isProfessionalView ? data.staffImage : null) || HERO_PLACEHOLDER;
  
  // Determine if we should show the hero section
  // For professional view, only show hero if there's a valid staff image
  // For salon view, always show hero (even with placeholder)
  const hasValidHeroImage = isProfessionalView
    ? isValidImage(data.staffImage)
    : true;
  
  // SEO: Build meta tags and JSON-LD structured data
  const seoTitle = `${data.name} - Book Online | Beu`;
  const seoCity = data.neighborhood || data.administrativeAreaLevel2 || data.administrativeAreaLevel1 || '';
  const seoServiceCount = data.stats?.totalServices || data.services?.length || 0;
  const seoDescription = data.description
    ? data.description.substring(0, 160)
    : `Book online at ${data.name}${seoCity ? ` in ${seoCity}` : ''}. ${seoServiceCount} services available.${data.rating ? ` Rated ${data.rating}/5.` : ''} Book instantly on Beu.`;
  const seoCanonicalUrl = data.slug ? `https://app.beuapp.com/book/${data.slug}` : '';
  const seoImage = data.heroImages?.[0] || '';

  // Map businessType to Schema.org type
  const schemaTypeMap = {
    hair_salon: 'HairSalon',
    barber: 'BarberShop',
    beauty_salon: 'BeautySalon',
    nail_salon: 'NailSalon',
    spa: 'DaySpa',
    other: 'HealthAndBeautyBusiness'
  };
  const schemaType = schemaTypeMap[data.businessType] || 'HealthAndBeautyBusiness';

  // Build JSON-LD structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    name: data.name,
    ...(seoCanonicalUrl && { url: seoCanonicalUrl }),
    ...(seoImage && { image: seoImage }),
    address: {
      '@type': 'PostalAddress',
      streetAddress: data.address,
      ...(seoCity && { addressLocality: seoCity }),
      ...(data.administrativeAreaLevel1 && { addressRegion: data.administrativeAreaLevel1 }),
      ...(data.postalCode && { postalCode: data.postalCode }),
      ...(data.countryLongName && { addressCountry: data.countryLongName })
    },
    ...(data.coordinates && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: data.coordinates.lat,
        longitude: data.coordinates.lng
      }
    }),
    ...(data.rating > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: data.rating,
        reviewCount: data.reviewCount || 1
      }
    }),
    ...(data.onlineBookingContactPhone && { telephone: data.onlineBookingContactPhone }),
    ...(data.website && { sameAs: data.website }),
    ...(data.rawOpeningHours?.length > 0 && {
      openingHoursSpecification: data.rawOpeningHours
        .filter(h => !h.isClose)
        .map(h => ({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: h.dayName,
          opens: h.sTime,
          closes: h.eTime
        }))
    })
  };

  return (
    <div className="salon-profile-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="business.business" />
        {seoImage && <meta property="og:image" content={seoImage} />}
        {seoCanonicalUrl && <meta property="og:url" content={seoCanonicalUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
        {seoCanonicalUrl && <link rel="canonical" href={seoCanonicalUrl} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={closeToast}
      />
      
      {/* Sticky Header */}
      <header className={`sticky-header ${isHeaderVisible ? 'visible' : ''}`}>
        <div className="sticky-header-content">
          {showBackButton && (
            <button
              className="back-button sticky-back-button"
              onClick={handleBack}
              onTouchEnd={(e) => { e.preventDefault(); handleBack(); }}
              aria-label="Go back"
            >
              <BackArrowIcon />
            </button>
          )}
          <h1 className="sticky-header-title">{data.name}</h1>
          <div className="sticky-header-actions">
            <button
              className="action-button"
              aria-label="Share"
              onClick={handleShare}
              onTouchEnd={(e) => { e.preventDefault(); handleShare(); }}
            >
              <ShareIcon />
            </button>
            <button
              className={`action-button favorite-button ${isAnimatingHeart ? 'animating' : ''}`}
              aria-label="Save"
              onClick={handleFavorite}
              onTouchEnd={(e) => { e.preventDefault(); handleFavorite(); }}
            >
              <HeartIcon filled={isFavorited} />
            </button>
          </div>
        </div>
        <nav className="sticky-nav">
          {navItems.map((item) => (
            <button
              key={item}
              className={`sticky-nav-item ${activeSection === item ? 'active' : ''}`}
              onClick={() => (item === 'Gift Cards' ? openGiftCards() : scrollToSection(item))}
            >
              {item}
            </button>
          ))}
        </nav>
      </header>
      
      {/* No Hero Header - show when there's no hero image */}
      {!hasValidHeroImage && (
        <header className={`no-hero-header ${!showBackButton ? 'no-back-button' : ''}`}>
          {showBackButton && (
            <button
              className="back-button"
              onClick={handleBack}
              onTouchEnd={(e) => { e.preventDefault(); handleBack(); }}
              aria-label="Go back"
            >
              <BackArrowIcon />
            </button>
          )}
          <div className="hero-actions">
            <button
              className="action-button"
              aria-label="Share"
              onClick={handleShare}
              onTouchEnd={(e) => { e.preventDefault(); handleShare(); }}
            >
              <ShareIcon />
            </button>
            <button
              className={`action-button favorite-button ${isAnimatingHeart ? 'animating' : ''}`}
              aria-label="Save"
              onClick={handleFavorite}
              onTouchEnd={(e) => { e.preventDefault(); handleFavorite(); }}
            >
              <HeartIcon filled={isFavorited} />
            </button>
          </div>
        </header>
      )}
      
      {/* Hero Section - only show if there's a valid hero image */}
      {hasValidHeroImage && (
        <section className="hero-section">
          {/* Professionals' hero images are portfolio shots, which are often
              portrait. Cover-cropping one into this wide banner shows a thin
              horizontal slice through the middle and cuts off the hair, which
              is the whole subject. The pro view therefore fits the whole image
              and fills the gap with a blurred copy of itself. Salons keep
              cover: their images are already wide banners. */}
          <div
            className={`hero-carousel${isProfessionalView ? ' hero-carousel--fit' : ''}${heroImages.length > 1 ? ' is-draggable' : ''}${heroDragStart !== null ? ' is-dragging' : ''}`}
            onTouchStart={handleHeroTouchStart}
            onTouchMove={handleHeroTouchMove}
            onTouchEnd={handleHeroTouchEnd}
            onMouseDown={heroImages.length > 1 || isProfessionalView ? handleHeroMouseDown : undefined}
            onMouseMove={heroImages.length > 1 || isProfessionalView ? handleHeroMouseMove : undefined}
          >
            <div
              className="hero-carousel-track"
              style={{
                // The live drag offset rides on top of the slide position, so
                // the image follows the cursor instead of only snapping on release.
                transform: `translateX(calc(-${currentImageIndex * 100}% + ${heroDragDelta}px))`,
              }}
            >
              {heroImages.map((image, index) => (
                <div key={index} className="hero-carousel-slide">
                  {/* Backdrop only on the visible slide and its neighbours:
                      each backdrop is a full-size image behind a 28px GPU
                      blur, and rendering one per slide meant a 5-photo
                      portfolio composited 5 blur layers on page open - real
                      jank on phones. Neighbours stay mounted so a swipe
                      never shows an unblurred edge mid-drag. */}
                  {isProfessionalView && Math.abs(index - currentImageIndex) <= 1 && (
                    <span
                      className="hero-image-backdrop"
                      style={{ backgroundImage: `url(${image})` }}
                      aria-hidden
                    />
                  )}
                  <img
                    src={image}
                    alt={`${data.name} ${index + 1}`}
                    className="hero-image"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    // These are full-size originals until the backend serves
                    // resized variants - decode off the main thread so a
                    // 4000px photo doesn't stall scrolling while it arrives.
                    decoding="async"
                    // A photo that fails to load shows the backdrop, not a broken icon.
                    onError={(e) => { if (e.currentTarget.src !== HERO_PLACEHOLDER) e.currentTarget.src = HERO_PLACEHOLDER; }}
                    // First image is the LCP element on this route -
                    // fetchpriority=high tells the browser to prioritise it
                    // over other page resources.
                    {...(index === 0 ? { fetchpriority: 'high' } : {})}
                  />
                </div>
              ))}
            </div>
            
            <div className={`hero-nav-buttons ${!showBackButton ? 'no-back-button' : ''}`}>
              {showBackButton && (
                <button
                  className="back-button"
                  onClick={handleBack}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); handleBack(); }}
                  aria-label="Go back"
                >
                  <BackArrowIcon />
                </button>
              )}
              <div className="hero-actions">
                <button
                  className="action-button"
                  aria-label="Share"
                  onClick={handleShare}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); handleShare(); }}
                >
                  <ShareIcon />
                </button>
                <button
                  className={`action-button favorite-button ${isAnimatingHeart ? 'animating' : ''}`}
                  aria-label="Save"
                  onClick={handleFavorite}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); handleFavorite(); }}
                >
                  <HeartIcon filled={isFavorited} />
                </button>
              </div>
            </div>
            
            {heroImages.length > 1 && (
              <div className="carousel-indicator">
                {currentImageIndex + 1} / {heroImages.length}
              </div>
            )}
            
            {/* Hero carousel navigation arrows - desktop only */}
            {heroImages.length > 1 && (
              <>
                {currentImageIndex > 0 && (
                  <button
                    className="hero-carousel-arrow hero-carousel-arrow-prev"
                    onClick={goToHeroPrev}
                    aria-label="Previous image"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                )}
                {currentImageIndex < heroImages.length - 1 && (
                  <button
                    className="hero-carousel-arrow hero-carousel-arrow-next"
                    onClick={goToHeroNext}
                    aria-label="Next image"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        </section>
      )}
      
      {/* Content Section */}
      <main className={`profile-content ${!hasValidHeroImage ? 'no-hero' : ''}`} ref={contentRef}>
        {/* Profile Header */}
        <header className={`profile-header ${isProfessionalView ? 'professional-header' : ''}`}>
          {isProfessionalView ? (
            <>
              {/* Professional View: Staff picture on left, name on right */}
              <div className="professional-header-content">
                <div className="professional-avatar-container">
                  {isValidImage(data.staffImage) ? (
                    <AvatarWithFallback
                      src={data.staffImage}
                      alt={data.name}
                      className="professional-avatar"
                      initialsClassName="professional-avatar-initials"
                      initials={getInitials(data.name)}
                    />
                  ) : (
                    <div className="professional-avatar-initials">
                      {getInitials(data.name)}
                    </div>
                  )}
                </div>
                <div className="professional-info">
                  {data.jobTitle && (
                    <span className="employment-badge">{data.jobTitle}</span>
                  )}
                  <h1 className="profile-name">{data.name}</h1>
                  <div
                    className="rating-container clickable"
                    onClick={() => setIsReviewsModalOpen(true)}
                  >
                    <span className="rating-score">{data.rating}</span>
                    <div className="rating-stars">
                      {renderStars(data.rating)}
                    </div>
                    <span className="review-count">
                      ({data.reviewCount} {data.reviewCount === 1 ? 'review' : 'reviews'})
                    </span>
                  </div>
                </div>
              </div>
              <button
                className={`book-button${canBook ? '' : ' fully-booked'}`}
                onClick={canBook ? handleBook : undefined}
                disabled={!canBook}
              >
                {bookCtaLabel('Book')}
              </button>
            </>
          ) : (
            <>
              {/* Salon View: Standard layout */}
              <h1 className="profile-name">{data.name}</h1>
              {data.otherLocations && data.otherLocations.length > 0 && (
                <div className="profile-also-at" aria-label="Other locations">
                  <span className="profile-also-at-label">Also at</span>
                  {data.otherLocations.map((o) => (
                    <Link key={String(o.salonId || o.slug)} to={`/book/${o.slug}`} className="profile-also-at-chip">
                      <span className="profile-also-at-name">{o.salonName}</span>
                      {o.area && <span className="profile-also-at-area">{o.area}</span>}
                    </Link>
                  ))}
                </div>
              )}
              <div
                className="rating-container clickable"
                onClick={() => setIsReviewsModalOpen(true)}
              >
                <span className="rating-score">{data.rating}</span>
                <div className="rating-stars">
                  {renderStars(data.rating)}
                </div>
                <span className="review-count">
                  ({data.reviewCount} {data.reviewCount === 1 ? 'review' : 'reviews'})
                </span>
              </div>
              <button
                className={`book-button${canBook ? '' : ' fully-booked'}`}
                onClick={canBook ? handleBook : undefined}
                disabled={!canBook}
              >
                {bookCtaLabel('Book')}
              </button>
            </>
          )}
        </header>

        {/* A discount code from a link or an earlier visit: said in the first
            screen, before the services it covers */}
        {DISCOUNTS_VISIBLE && <CodeBanner code={bookingCode} />}
        
        {/* Bio/Description */}
        {data.description && (
          <div className="profile-description">{data.description}</div>
        )}
        
        {/* The professional's portfolio used to repeat here as a thumbnail
            strip. It is the hero carousel at the top of the page now, so this
            was the same images twice on one screen. Professional view only —
            the salon view never rendered this row. */}

        {/* Gallery images are now shown in the hero carousel for salon view */}
        
        {/* Services Section - Hidden when no online services available */}
        {hasOnlineServices && (
        <section className="section" ref={servicesRef}>
          <h2 className="section-title">Services</h2>
          <div className="category-tabs">
            {data.serviceCategories.map((category) => (
              <button
                key={category}
                className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          
          {filteredServices.map((service) => (
            <div
              key={service.id}
              id={`service-${service.id}`}
              className={`service-card ${selectedServices.some(s => s.id === service.id) ? 'selected' : ''}`}
              onClick={() => handleServiceClick(service)}
              style={{ cursor: 'pointer' }}
            >
              <div className="service-top-row">
                <div className="service-info">
                  <h3 className="service-name">{service.name}</h3>
                  <p className="service-duration">{service.duration}</p>
                  {DISCOUNTS_VISIBLE && <CodeTag code={bookingCode} service={service} />}
                </div>
                <div className="service-right">
                  <span className="service-price">from £{(service.fromPrice ?? service.price).toFixed(2)}</span>
                  <button
                    className={`add-service-button ${selectedServices.some(s => s.id === service.id) ? 'added' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const isSelected = selectedServices.some(s => s.id === service.id);
                      if (isSelected) {
                        // If already selected, just toggle it off (unselect)
                        toggleServiceForBooking(service);
                      } else {
                        // If not selected, add it and open the modal
                        handleServiceCardClick(service);
                      }
                    }}
                    aria-label={selectedServices.some(s => s.id === service.id) ? 'Remove service' : 'Add service'}
                  >
                    {selectedServices.some(s => s.id === service.id) ? <CheckIcon /> : <PlusIcon />}
                  </button>
                </div>
              </div>
              {service.description && (
                <p style={{
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: '1.5',
                  margin: '10px 0 0 0',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  overflow: 'hidden',
                  display: 'block',
                  width: '100%',
                  boxSizing: 'border-box',
                }}>
                  {service.description.length > 95
                    ? service.description.substring(0, 95) + '...'
                    : service.description}
                </p>
              )}
            </div>
          ))}
          
          <button className="view-all-button" onClick={openServicesModal}>All Services</button>
        </section>
        )}
        
        {/* Team Section (Salon view only) - Hidden when no online services available */}
        {!isProfessionalView && hasOnlineServices && data.team && (
          <section className="section" ref={teamRef}>
            <h2 className="section-title">Team</h2>
            {(showAllTeamMembers ? data.team : data.team.slice(0, 4)).map((member) => (
              // Still LISTED when online booking is off — the team is useful
              // information — but not a route into a booking flow that would
              // dead-end. No pointer, no button role, not focusable.
              <div
                key={member.id}
                className={`team-card${bookingOpen ? ' team-card-clickable' : ''}`}
                onClick={bookingOpen ? () => handleTeamMemberClick(member.id, member.slug) : undefined}
                role={bookingOpen ? 'button' : undefined}
                tabIndex={bookingOpen ? 0 : undefined}
                onKeyPress={bookingOpen ? (e) => e.key === 'Enter' && handleTeamMemberClick(member.id, member.slug) : undefined}
              >
                {isValidImage(member.image) ? (
                  <AvatarWithFallback
                    src={member.image}
                    alt={member.name}
                    className="team-avatar"
                    initialsClassName="team-avatar-initials"
                    initials={getInitials(member.name)}
                  />
                ) : (
                  <div className="team-avatar-initials">
                    {getInitials(member.name)}
                  </div>
                )}
                <div className="team-info">
                  <div className="team-header">
                    <h3 className="team-name">{member.name}</h3>
                    <div className="team-rating">
                      {member.rating} <StarIcon filled={true} /> <span>({member.reviewCount})</span>
                    </div>
                  </div>
                  <p className="team-role">{member.role}</p>
                  <p className="team-description">{member.description}</p>
                </div>
              </div>
            ))}
            {data.team.length > 4 && (
              <button
                className="view-all-button"
                onClick={() => setShowAllTeamMembers(!showAllTeamMembers)}
              >
                {showAllTeamMembers ? 'Show Less' : 'All Team Members'}
              </button>
            )}
          </section>
        )}
        
        {/* TEMPORARILY COMMENTED OUT - Shop Section
        {!isProfessionalView && (
          <section className="section" ref={shopRef}>
            <h2 className="section-title">Shop</h2>
            {data.products && data.products.length > 0 ? (
              <>
                <div className="category-tabs">
                  <button
                    className={`category-tab ${selectedProductCategory === 'All' ? 'active' : ''}`}
                    onClick={() => setSelectedProductCategory('All')}
                  >
                    All
                  </button>
                  {[...new Set(data.products.map(p => p.category).filter(Boolean))].map((category) => (
                    <button
                      key={category}
                      className={`category-tab ${selectedProductCategory === category ? 'active' : ''}`}
                      onClick={() => setSelectedProductCategory(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                <div className="products-grid">
                  {data.products
                    .filter(product => selectedProductCategory === 'All' || product.category === selectedProductCategory)
                    .map((product) => (
                      <div key={product.id} className="product-card">
                        <img src={product.image} alt={product.name} className="product-image" />
                        <div className="product-info">
                          <h3 className="product-name">{product.name}</h3>
                          <p className="product-price">£{(product.price || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                </div>
                <button className="view-all-button" onClick={() => setIsShopModalOpen(true)}>View Shop</button>
              </>
            ) : (
              <p className="no-items-message">No products available at this time.</p>
            )}
          </section>
        )}
        */}
        
        {/* TEMPORARILY COMMENTED OUT - Vouchers Section
        {!isProfessionalView && hasOnlineVouchers && (
          <section className="section" ref={vouchersRef}>
            <h2 className="section-title">Vouchers</h2>
            <div className="voucher-card">
              <div className="voucher-placeholder">Voucher</div>
              <div className="voucher-info">
                <h4>{data.vouchers.description}</h4>
                <p>{data.vouchers.benefits}</p>
              </div>
            </div>
            <button className="view-all-button" style={{ marginTop: '16px' }} onClick={() => setIsVouchersModalOpen(true)}>View Vouchers</button>
          </section>
        )}
        */}
        
        {/* Contact Us Section - Only show if phone or email is provided */}
        {(data.onlineBookingContactPhone || data.onlineBookingContactEmail) && (
          <section className="section contact-section">
            <h2 className="section-title">Contact Us</h2>
            <div className="contact-buttons">
              {data.onlineBookingContactPhone && (
                <a
                  href={`tel:${data.onlineBookingContactPhone}`}
                  className="contact-cta call-cta"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <span>Call</span>
                </a>
              )}
              {data.onlineBookingContactEmail && (
                <a
                  href={`mailto:${data.onlineBookingContactEmail}`}
                  className="contact-cta email-cta"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <span>Email</span>
                </a>
              )}
            </div>
          </section>
        )}
        
        {/* Gift Cards Section. The salon's master "sell online" switch gates the
            BUY experience (teaser + CTA + the /gift-cards shop). The ownership
            cue stays regardless, so a client can still use a card they already
            hold. NOTE (CTO): `getGiftCardChannels()` is a localStorage mock —
            swap for the salon's real `giftCardChannels.online` from the summary.
            The whole section hides when there's nothing to show (selling off and
            either logged out or no owned card to surface). */}
        {!isProfessionalView && (getGiftCardChannels().online || localStorage.getItem('accessToken')) && (
          <section className="section" ref={giftCardsRef}>
            <h2 className="section-title">Gift Cards</h2>
            {localStorage.getItem('accessToken') && (
              <Suspense fallback={null}>
                <GiftCardOwnershipCue
                  salonId={salonData?.id || salonIdOrSlug}
                  salonSlug={salonData?.slug || salonIdOrSlug}
                  currency={data?.currencySymbol || '£'}
                  onUse={handleBook}
                />
              </Suspense>
            )}
            {getGiftCardChannels().online && (
              <>
                <div
                  className="team-card team-card-clickable giftcard-teaser"
                  role="button"
                  tabIndex={0}
                  onClick={openGiftCards}
                  onKeyPress={(e) => e.key === 'Enter' && openGiftCards()}
                >
                  <div className="giftcard-teaser-icon" aria-hidden style={{ width: 60, height: 60, borderRadius: '50%', background: '#f3eadd', color: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="8" width="18" height="13" rx="1.5" />
                      <path d="M3 12h18M12 8v13" />
                      <path d="M12 8S10.5 3.5 8 4.5 9.5 8 12 8zM12 8s1.5-4.5 4-3.5S14.5 8 12 8z" />
                    </svg>
                  </div>
                  <div className="team-info" style={{ minWidth: 0 }}>
                    <h3 className="team-name">Give the gift of a treat</h3>
                    <p className="team-role" style={{ whiteSpace: 'normal' }}>Buy a gift card, delivered by email and ready to spend.</p>
                  </div>
                </div>
                <button className="view-all-button" style={{ marginTop: '16px' }} onClick={openGiftCards}>
                  Buy a gift card
                </button>
              </>
            )}
          </section>
        )}

        {/* Shop — inline product preview (mirrors the Services section: tabs +
            grid + "View Shop"). Renders nothing when no products sell online.
            NOTE (CTO): `getProductChannels()` is a localStorage mock — swap for
            the salon's real product online flag from the summary. */}
        {!isProfessionalView && getProductChannels().online && (
          <ProductShopPreview
            salonIdOrSlug={salonIdOrSlug}
            onOpenShop={() => openProductShop()}
            onOpenProduct={(id) => openProductShop(id)}
          />
        )}

        {/* Info Section */}
        <section className="section info-section" ref={infoRef}>
          <h2 className="section-title">Info</h2>
          
          <div className="hours-list">
            {Object.entries(data.openingHours).map(([day, hours]) => (
              <div key={day} className="hours-row">
                <span className={`hours-day ${!hours.open ? 'closed' : ''}`}>{day}</span>
                <span className={`hours-time ${!hours.open ? 'closed' : ''}`}>
                  {hours.open ? `${hours.from} - ${hours.to}` : (isProfessionalView ? 'Not working' : 'Closed')}
                </span>
              </div>
            ))}
          </div>
          
          <p className="address-text">{data.address}</p>
          
          <LazyMap lat={data.coordinates.lat} lng={data.coordinates.lng} />
          
          <a 
            href={`https://www.google.com/maps/dir/?api=1&destination=${data.coordinates.lat},${data.coordinates.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="directions-link"
          >
            Get Directions
          </a>
        </section>
        
        {/* Other locations: sister sites the owner chose to show */}
        {hasOtherLocations && (
          <section className="section locations-section" ref={locationsRef}>
            <h2 className="section-title">Our other locations</h2>
            <div className="locations-grid">
              {data.otherLocations.map((o) => (
                <Link key={String(o.salonId || o.slug)} to={`/book/${o.slug}`} className="location-card">
                  <div className="location-card-media">
                    {isValidImage(o.image) ? (
                      <img src={o.image} alt="" loading="lazy" />
                    ) : (
                      <div className="location-card-fallback" aria-hidden="true">{(o.salonName || '?').charAt(0).toUpperCase()}</div>
                    )}
                  </div>
                  <div className="location-card-body">
                    <div className="location-card-text">
                      <h3 className="location-card-name">{o.salonName}</h3>
                      {(o.address || o.area) && <p className="location-card-area">{o.address || o.area}</p>}
                    </div>
                    <span className="location-card-cta">Book</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      
      {/* Booking Flow Modals — each wrapped in its own Suspense boundary so a
          cold-loading modal can't suspend/unmount an already-open sibling.
          The outer `{isXOpen && …}` guard keeps the modal out of the tree
          (and off the download path) until the user first opens it. */}
      {isStylistSelectionOpen && (
        <Suspense fallback={null}>
          <StylistSelectionModal
            isOpen={isStylistSelectionOpen}
            onClose={() => setIsStylistSelectionOpen(false)}
            onBack={() => setIsStylistSelectionOpen(false)}
            team={
              // Filter team to only show stylists who offer ALL selected services
              // Use servicesToFilterBy which is set synchronously before opening the modal
              servicesToFilterBy.length > 0
                ? (data?.team || []).filter(member => {
                    // Check if this team member can perform all selected services
                    return servicesToFilterBy.every(service => {
                      // Get hairdresser IDs from both possible arrays
                      let serviceHairdresserIds = [];

                      // Check hairdressers array (array of objects with _id property)
                      if (service.hairdressers && Array.isArray(service.hairdressers) && service.hairdressers.length > 0) {
                        serviceHairdresserIds = service.hairdressers.map(h => h._id);
                      }

                      // If no hairdressers found, check hairdresserIds array (array of strings)
                      if (serviceHairdresserIds.length === 0 && service.hairdresserIds && Array.isArray(service.hairdresserIds)) {
                        serviceHairdresserIds = service.hairdresserIds;
                      }

                      // If no hairdressers defined for this service, assume all can do it
                      if (serviceHairdresserIds.length === 0) {
                        return true;
                      }

                      // Check if this member's ID is in the service's hairdressers list
                      return serviceHairdresserIds.includes(member.id);
                    });
                  })
                : (data?.team || [])
            }
            onSelectStylist={handleStylistSelect}
            onViewProfile={handleViewStylistProfile}
            salonImage={data?.heroImages?.[0] || ''}
          />
        </Suspense>
      )}

      {isServicesModalOpen && (
        <Suspense fallback={null}>
          <ServicesModal
            bookingCode={DISCOUNTS_VISIBLE ? bookingCode : null}
            isOpen={isServicesModalOpen}
            onClose={() => setIsServicesModalOpen(false)}
            onBack={() => {
              setIsServicesModalOpen(false);
              if (!isProfessionalView) {
                // Only show stylist selection if there's more than 1 stylist available
                const availableStylists = getAvailableStylists();
                if (availableStylists.length > 1) {
                  // Set the services to filter by before opening the modal
                  setServicesToFilterBy(selectedServices);
                  setIsStylistSelectionOpen(true);
                }
              }
            }}
            services={data?.services || []}
            serviceCategories={data?.serviceCategories || []}
            selectedServices={selectedServices}
            selectedStylistId={selectedStylist?.id || bookingDetails?.stylist?.id || null}
            onServiceToggle={toggleServiceForBooking}
            onServiceClick={handleServiceClick}
            onChooseSlot={handleChooseSlot}
            currencySymbol="£"
            desiredCategory={selectedCategory}
          />
        </Suspense>
      )}

      {isServiceDetailOpen && (
        <Suspense fallback={null}>
          <ServiceDetailPopup
            isOpen={isServiceDetailOpen}
            onClose={() => setIsServiceDetailOpen(false)}
            onBack={() => {
              setIsServiceDetailOpen(false);
            }}
            service={selectedServiceForDetail}
            selectedStylistId={selectedStylist?.id || bookingDetails?.stylist?.id || null}
            onAddToBooking={handleAddToBooking}
            currencySymbol="£"
          />
        </Suspense>
      )}

      {isTimesModalOpen && (
        <Suspense fallback={null}>
          <TimesModal
            isOpen={isTimesModalOpen}
            onClose={() => setIsTimesModalOpen(false)}
            onBack={() => {
              setIsTimesModalOpen(false);
              setIsServicesModalOpen(true);
            }}
            selectedServices={selectedServices}
            team={isProfessionalView ? (data?.team || NO_TEAM) : timesTeam}
            initialStylist={isProfessionalView ? data?.staffAsStylist : selectedStylist}
            onCheckout={handleCheckout}
            currencySymbol="£"
            salonId={isProfessionalView ? data?.salonId : (data?.id || salonIdOrSlug)}
            getServicePrice={getStylistPrice}
          />
        </Suspense>
      )}

      {isConfirmBookingOpen && (
        <Suspense fallback={null}>
          <ConfirmBookingModal
            isOpen={isConfirmBookingOpen}
            onClose={() => {
              setIsConfirmBookingOpen(false);
              // Dismissing the sheet ends any payment-retry context — a new
              // flow from here must create a new booking, not pay an old one.
              paymentRetryBookingIdRef.current = null;
            }}
            onBack={() => {
              setIsConfirmBookingOpen(false);
              setIsTimesModalOpen(true);
              paymentRetryBookingIdRef.current = null;
            }}
            selectedServices={selectedServices}
            bookingDetails={bookingDetails}
            onConfirm={handleConfirmBooking}
            currencySymbol="£"
            getServicePrice={getStylistPrice}
            salonId={isProfessionalView ? data?.salonId : (data?.id || salonIdOrSlug)}
            salonSlug={data?.slug || salonIdOrSlug}
            consentContext={consentContext}
            salonName={isProfessionalView ? (data?.salonName || data?.name) : data?.name}
            // In-sheet steps for logged-out clients (auth → guest details → OTP)
            step={confirmStep}
            onStepChange={handleConfirmStepChange}
            pendingBookingData={pendingBookingData}
            guestBookingAllowed={guestGate.allowed}
            guestBlockedByPayment={guestGate.blockedByPayment}
            onContinueAsGuest={() => {
              // Keep pendingBookingData; the details step submits it.
              setGuestSubmitError('');
              setConfirmStep('guest-details');
            }}
            guestInitialValues={pendingGuest?.details || null}
            guestSubmitting={guestSubmitting}
            guestSubmitError={guestSubmitError}
            onGuestSubmit={handleGuestSubmit}
            otpVariant={guestOtpVariant}
            otpSentTo={guestOtpSentTo}
            otpSubmitting={guestOtpSubmitting}
            otpError={guestOtpError}
            onOtpVerify={handleGuestOtpVerify}
            onOtpResend={handleGuestOtpResend}
            bookingCode={DISCOUNTS_VISIBLE ? bookingCode : null}
          />
        </Suspense>
      )}

      {isReviewsModalOpen && (
        <Suspense fallback={null}>
          <ReviewsModal
            isOpen={isReviewsModalOpen}
            onClose={() => setIsReviewsModalOpen(false)}
            salonId={isProfessionalView ? data?.salonId : (data?.id || salonIdOrSlug)}
            hairdresserId={isProfessionalView ? hairdresserId : null}
            entityName={data?.name || ''}
            entityImage={isProfessionalView ? data?.staffImage : data?.heroImages?.[0]}
            initialRating={data?.rating || 0}
            initialReviewCount={data?.reviewCount || 0}
          />
        </Suspense>
      )}

      {/* TEMPORARILY COMMENTED OUT - Shop and Vouchers Modals
      <ShopModal
        isOpen={isShopModalOpen}
        onClose={() => setIsShopModalOpen(false)}
        products={data?.products || []}
        onProductClick={(product) => {
          setSelectedProduct(product);
          setIsProductDetailOpen(true);
        }}
      />

      <ProductDetailModal
        isOpen={isProductDetailOpen}
        onClose={() => {
          setIsProductDetailOpen(false);
          setSelectedProduct(null);
        }}
        onBack={() => {
          setIsProductDetailOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        relatedProducts={(data?.products || []).filter(p => p.id !== selectedProduct?.id).slice(0, 4)}
        onProductClick={(product) => {
          setSelectedProduct(product);
        }}
        hairdresserId={isProfessionalView ? hairdresserId : null}
        entityName={data?.name || ''}
        entityImage={isProfessionalView ? data?.staffImage : data?.heroImages?.[0]}
        initialRating={data?.rating || 0}
        initialReviewCount={data?.reviewCount || 0}
      />
      
      <VouchersModal
        isOpen={isVouchersModalOpen}
        onClose={() => setIsVouchersModalOpen(false)}
        vouchers={data?.vouchers?.items || []}
      />
      */}
      
      {isAuthPromptOpen && (
        <Suspense fallback={null}>
          <AuthPromptModal
            isOpen={isAuthPromptOpen}
            onClose={() => {
              setIsAuthPromptOpen(false);
              setPendingBookingData(null);
              setAuthPromptContext('booking');
            }}
            pendingBookingData={pendingBookingData}
            context={authPromptContext}
          />
        </Suspense>
      )}

      {isConsultationGateOpen && (
        <Suspense fallback={null}>
          <ConsultationGateModal
            isOpen={isConsultationGateOpen}
            onClose={() => setIsConsultationGateOpen(false)}
            gates={consultationGates}
            onYes={handleConsultationYes}
            onNo={handleConsultationNo}
          />
        </Suspense>
      )}

      {isFinishingGateOpen && (
        <Suspense fallback={null}>
          <FinishingGateModal
            isOpen={isFinishingGateOpen}
            onClose={handleFinishingClose}
            gate={activeFinishingGate}
            onPick={handleFinishingPick}
            currencySymbol={data?.currencySymbol || '£'}
          />
        </Suspense>
      )}

      {/* Full-size image viewer, opened by tapping the hero. It is passed
          heroImages rather than galleryImages because it must show exactly
          what was on screen when tapped — same portfolio list, but heroImages
          is the one that carries the avatar/placeholder fallbacks. */}
      {isPortfolioGalleryOpen && (
        <Suspense fallback={null}>
          <PortfolioGalleryModal
            isOpen={isPortfolioGalleryOpen}
            onClose={() => setIsPortfolioGalleryOpen(false)}
            images={data?.heroImages || data?.galleryImages || []}
            initialIndex={portfolioGalleryInitialIndex}
          />
        </Suspense>
      )}

      <BookingLoader
        isVisible={bookingLoaderVisible}
        state={bookingLoaderState}
        chargeNow={bookingChargeNow}
      />
      
      {/* Sticky Book Button - appears when scrolling (opposite animation to header).
          Rendered even when the stylist/salon has no bookable services so the
          disabled "Fully Booked" state communicates why booking isn't available
          rather than silently hiding the button. */}
      {data && (
        <div className={`sticky-book-button-container ${isHeaderVisible ? 'visible' : ''}`}>
          <button
            className={`sticky-book-button${canBook ? '' : ' fully-booked'}`}
            onClick={canBook ? handleBook : undefined}
            disabled={!canBook}
          >
            {bookCtaLabel('Book Now')}
          </button>
        </div>
      )}
      {/* AI Concierge — renders its bubble only for salons that enabled it
          (widget hides itself permanently when the BE says not-enabled).
          Out of the way while any booking step or sheet is open, so it never
          covers their footers, and above the sticky Book Now bar when that
          is up.
          On a self-employed pro's own link (/book/pro/:slug) it runs as
          THEIR receptionist: the widget names and proves the page
          (proHairdresserId = the stylist's user id, proSlug = their slug at
          this salon), the API binds the chat to that stylist from the first
          message (hidden from the salon unless the client is shared) and
          refuses (page_unverified) without both — so no proof, no bubble.
          Still NOT on /book/:slug/professional/:id: that page belongs to the
          salon, and the salon page's bubble is one tap away. */}
      {CONCIERGE_VISIBLE && !isProfessionalView && salonData?.id && (
        <ConciergeWidget
          salonId={salonData.id}
          salonName={salonData.name}
          // Same page context the booking flow sends to createOnlineBooking,
          // so a chat booking's client ownership follows the same rule.
          acquisitionChannel="salon_profile"
          lifted={!!data && isHeaderVisible}
          hidden={
            isStylistSelectionOpen || isServicesModalOpen || isServiceDetailOpen || isTimesModalOpen
            || isConfirmBookingOpen || isAuthPromptOpen || isReviewsModalOpen || isShopModalOpen
            || isVouchersModalOpen || isProductDetailOpen || bookingLoaderVisible
          }
        />
      )}
      {CONCIERGE_VISIBLE && isProRoute && salonData?.salonId && salonData?.staffAsStylist?.id && salonData?.staffAsStylist?.slug && (
        <ConciergeWidget
          salonId={salonData.salonId}
          salonName={salonData.name}
          acquisitionChannel="pro_link"
          proHairdresserId={salonData.staffAsStylist.id}
          proSlug={salonData.staffAsStylist.slug}
          lifted={!!data && isHeaderVisible}
          hidden={
            isStylistSelectionOpen || isServicesModalOpen || isServiceDetailOpen || isTimesModalOpen
            || isConfirmBookingOpen || isAuthPromptOpen || isReviewsModalOpen || isShopModalOpen
            || isVouchersModalOpen || isProductDetailOpen || bookingLoaderVisible
          }
        />
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const { hairdresser, salon, user, booking, salonList, createBooking } = state;
  return {
    hairdresser,
    salon,
    user,
    booking,
    salonList,
    createBooking,
  };
}

export default connect(mapStateToProps)(SalonProfilePage);
